import {
  Coding,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  Reference,
  ValueSetExpansionContains,
} from "@medplum/fhirtypes";

import { format } from "date-fns-tz";
import { isNumber } from "lodash";

import { getEncounterServiceDateDetailQuestionnaireItems } from "@/encounters/utils/encounter-service-date-form";
import { Extensions } from "@/fhir/enums/extensions";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import { Systems } from "@/fhir/enums/systems";
import { combineDateTimeInTimezone, combineDateTimeToUTC } from "@/utils/datetime";
import { joinReferenceString, setExtensionValue } from "@/utils/medplum";

import { MedsAndTherapies } from "../components/NewbornPostpartumEncounter/components/MedicationAndTherapiesDrawer";
import {
  NewbornPostpartumInputsOutputsQuestionsDisplayNames,
  NewbornPostpartumInputsOutputsQuestionsLinkIds,
} from "../constants/newborn-postpartum";
import {
  PostpartumEmotionalHealthQuestionsDisplayNames,
  PostpartumEmotionalHealthQuestionsLinkIds,
  PostpartumFeedingQuestionsDisplayNames,
  PostpartumFeedingQuestionsLinkIds,
  PostpartumLochiaQuestionsDisplayNames,
  PostpartumLochiaQuestionsLinkIds,
  PostpartumSexualActivityQuestionsDisplayNames,
  PostpartumSexualActivityQuestionsLinkIds,
  PostpartumSleepQuestionsDisplayNames,
  PostpartumSleepQuestionsLinkIds,
  PostpartumSocialHealthQuestionsDisplayNames,
  PostpartumSocialHealthQuestionsLinkIds,
} from "../constants/postpartum-encounter";
import { REASON_FOR_TRANSFER_LABEL } from "../constants/transfer-of-care";
import {
  EncounterAmendmentFormType,
  EncounterAmendmentInfoValuesType,
  EncounterAnswersFormType,
  EncounterDetailsFormType,
  EncounterDiagnosesFormType,
  PrenatalFlowsheetFormType,
  VitalSignsFormType,
} from "../zod-schemas/encounter-answers";
import { EncounterFormType } from "../zod-schemas/encounters";
import {
  GynecologicalAmendmentEncounterFormType,
  GynecologicalEncounterVitalsSignFormType,
} from "../zod-schemas/gyn-encounter";
import {
  NewbornEncounterBirthDetailsFormType,
  NewbornEncounterFeedingFormType,
  NewbornEncounterSizeFormType,
  NewbornIPPAmendmentEncounterFormType,
  NewbornIppEncounterVitalsSignFormType,
  NewbornIPPGestationalAgeFormType,
  NewbornIPPMedicationAndTherapiesFormType,
} from "../zod-schemas/newborn-ipp-encounter";
import {
  NewbornPostpartumAmendmentEncounterFormType,
  NewbornPostpartumEncounterSizeFormType,
  NewbornPostpartumEncounterStateOfNewbornFormType,
  NewbornPostpartumEncounterVitalsSignFormType,
} from "../zod-schemas/newborn-postpartum-encounter";
import {
  PostpartumAmendmentEncounterFormType,
  PostpartumVitalsFormType,
} from "../zod-schemas/postpartum-encounter";

const AMENDMENT_QUESTIONNAIRE_FIELD = "encounterAmendmentInfo";

type GetBaseQuestionnaireResponseParams<T> = {
  form: T;
  clientId: string;
};

type EncounterFormSerializationInput = Omit<EncounterFormType, "startTime"> & {
  startTime?: string;
};

type GetEncounterQuestionnaireResponseParams =
  GetBaseQuestionnaireResponseParams<EncounterFormSerializationInput> & {
    status: QuestionnaireResponse["status"];
    practitionerReference: Reference<Practitioner>;
    encounterId?: string;
    questionnaireId: string;
    /**
     * Canonical newborn reference for a standalone Newborn IPP/Postpartum creation, written as an
     * extension rather than `subject` — see {@link Extensions.NewbornPatientReference}.
     */
    newbornPatientReference?: Reference<Patient>;
  };

type GetNewbornEncounterQuestionnaireResponseParams =
  GetBaseQuestionnaireResponseParams<NewbornEncounterBirthDetailsFormType> & {
    practitionerId: string;
    encounterId: string;
    questionnaireId: string;
    status: QuestionnaireResponse["status"];
    questionnaireResponse?: QuestionnaireResponse;
  };

type GetNewbornEncounterSizeQuestionnaireResponseParams =
  GetBaseQuestionnaireResponseParams<NewbornEncounterSizeFormType> & {
    practitionerId: string;
    encounterId: string;
    questionnaireId: string;
    status: QuestionnaireResponse["status"];
    questionnaireResponse?: QuestionnaireResponse;
  };

type GetEncounterAnswersQuestionnaireResponseParams = {
  form: EncounterAnswersFormType | EncounterAmendmentFormType;
  status: QuestionnaireResponse["status"];
  clientId: string;
  practitionerReference: Reference<Practitioner>;
  questionnaire: Questionnaire;
  encounterId: string;
  questionnaireResponse?: QuestionnaireResponse;
};

type GetNewbornEncounterVitalsSignQuestionnaireResponseParams =
  GetBaseQuestionnaireResponseParams<NewbornIppEncounterVitalsSignFormType> & {
    practitionerId: string;
    encounterId: string;
    questionnaireId: string;
    status: QuestionnaireResponse["status"];
    questionnaireResponse?: QuestionnaireResponse;
  };

type GetNewbornIPPEncounterFeedingQuestionnaireResponseParams =
  GetBaseQuestionnaireResponseParams<NewbornEncounterFeedingFormType> & {
    practitionerId: string;
    encounterId: string;
    questionnaireId: string;
    status: QuestionnaireResponse["status"];
    questionnaireResponse?: QuestionnaireResponse;
  };

type GetNewbornIPPMedicationAndTherapiesQuestionnaireResponseParams =
  GetBaseQuestionnaireResponseParams<NewbornIPPMedicationAndTherapiesFormType> & {
    practitionerId: string;
    encounterId: string;
    questionnaireId: string;
    status: QuestionnaireResponse["status"];
    questionnaireResponse?: QuestionnaireResponse;
  };

type GetNewbornIPPGestationalAgeQuestionnaireResponseParams =
  GetBaseQuestionnaireResponseParams<NewbornIPPGestationalAgeFormType> & {
    practitionerId: string;
    encounterId: string;
    questionnaireId: string;
    status: QuestionnaireResponse["status"];
    questionnaireResponse?: QuestionnaireResponse;
    lmpGestationalAge?: number;
  };

type GetNewbornPostpartumEncounterSizeQuestionnaireResponseParams =
  GetBaseQuestionnaireResponseParams<NewbornPostpartumEncounterSizeFormType> & {
    practitionerId: string;
    encounterId: string;
    questionnaireId: string;
    status: QuestionnaireResponse["status"];
    questionnaireResponse?: QuestionnaireResponse;
  };

type GetNewbornPostpartumEncounterVitalsSignQuestionnaireResponseParams =
  GetBaseQuestionnaireResponseParams<NewbornPostpartumEncounterVitalsSignFormType> & {
    practitionerId: string;
    encounterId: string;
    questionnaireId: string;
    status: QuestionnaireResponse["status"];
    questionnaireResponse?: QuestionnaireResponse;
  };

type GetGynecologicalEncounterVitalsSignQuestionnaireResponseParams =
  GetBaseQuestionnaireResponseParams<GynecologicalEncounterVitalsSignFormType> & {
    practitionerId: string;
    encounterId: string;
    questionnaireId: string;
    status: QuestionnaireResponse["status"];
    questionnaireResponse?: QuestionnaireResponse;
  };

type GetNewbornPostpartumMedicationAndTherapiesQuestionnaireResponseParams =
  GetBaseQuestionnaireResponseParams<MedsAndTherapies> & {
    practitionerId: string;
    encounterId: string;
    questionnaireId: string;
    status: QuestionnaireResponse["status"];
    questionnaireResponse?: QuestionnaireResponse;
  };

type GetNewbornPostpartumInputsOutputsQuestionnaireResponseParams =
  GetBaseQuestionnaireResponseParams<{
    inputOutputFeedingType: string;
    inputOutputFeedingSupplementAmount: number;
    inputOutputFeedingLength: number;
    inputOutputFeedingBehavior: string[];
    inputOutputVoiding: string[];
    inputOutputVoidingNormal: string;
    inputOutputVoidingNotNormal: string;
    inputOutputBowel: string[];
    inputOutputBowelNormal: string;
    inputOutputBowelNotNormal: string;
    inputOutputNotes: string;
    inputOutputFeedingSupplement?: string[];
    inputOutputFeedingSupplementAmountPumpedMilk?: number;
    inputOutputFeedingSupplementAmountUnitPumpedMilk?: Coding;
    inputOutputFeedingSupplementAverageAmountPumpedMilk?: number;
    inputOutputFeedingSupplementAmountDonorMilk?: number;
    inputOutputFeedingSupplementAmountUnitDonorMilk?: Coding;
    inputOutputFeedingSupplementAverageAmountDonorMilk?: number;
    inputOutputFeedingSupplementAmountFormula?: number;
    inputOutputFeedingSupplementAmountUnitFormula?: Coding;
    inputOutputFeedingSupplementAverageAmountFormula?: number;
    inputOutputFeedingSupplementFormset?: Record<
      string,
      {
        inputOutputFeedingSupplementFormsetCode: string;
        inputOutputFeedingSupplementFormsetType?: string[];
        inputOutputFormsetTypeFeedingSupplementAmountPumpedMilk?: number;
        inputOutputFormsetTypeFeedingAmountUnitPumpedMilk?: Coding;
        inputOutputFormsetTypeFeedingAverageAmountPumpedMilk?: number;
        inputOutputFormsetTypeFeedingSupplementAmountDonorMilk?: number;
        inputOutputFormsetTypeFeedingAmountUnitDonorMilk?: Coding;
        inputOutputFormsetTypeFeedingAverageAmountDonorMilk?: number;
        inputOutputFormsetTypeFeedingSupplementAmountFormula?: number;
        inputOutputFormsetTypeFeedingAmountUnitFormula?: Coding;
        inputOutputFormsetTypeFeedingAverageAmountFormula?: number;
      }
    >;
  }> & {
    practitionerId: string;
    encounterId: string;
    questionnaireId: string;
    status: QuestionnaireResponse["status"];
    questionnaireResponse?: QuestionnaireResponse;
  };

type GetNewbornPostpartumStateOfNewbornEncounterQuestionnaireResponseParams =
  GetBaseQuestionnaireResponseParams<NewbornPostpartumEncounterStateOfNewbornFormType> & {
    practitionerId: string;
    encounterId: string;
    questionnaireId: string;
    status: QuestionnaireResponse["status"];
    questionnaireResponse?: QuestionnaireResponse;
    newbornStatesList: ValueSetExpansionContains[];
    sexOptionsList: ValueSetExpansionContains[];
  };

type GetPostpartumFeedingQuestionnaireResponseParams = {
  form: {
    postpartumFeedingType?: string[];
    postpartumFeedingSupplement?: string[];
    postpartumFeedingWith?: string[];
    postpartumFeedingSupply?: string;
    postpartumFeedingPain?: string;
    postpartumFeedingRedness?: string;
    postpartumFeedingDucts?: string;
    postpartumFeedingNotes?: string;
  };
  clientId: string;
  practitionerId: string;
  encounterId: string;
  questionnaireId: string;
  status: QuestionnaireResponse["status"];
  questionnaireResponse?: QuestionnaireResponse;
};

type GetPostpartumLochiaQuestionnaireResponseParams = {
  form: {
    postpartumLochiaType?: string[];
    postpartumLochiaNotes?: string;
  };
  clientId: string;
  practitionerId: string;
  encounterId: string;
  questionnaireId: string;
  status: QuestionnaireResponse["status"];
  questionnaireResponse?: QuestionnaireResponse;
};

type GetPostpartumActivityNutritionQuestionnaireResponseParams = {
  form: {
    postpartumActivityNutritionType?: string[];
    postpartumActivityNutritionTypeOther?: string;
    postpartumActivityNutritionDietaryRestrictions?: string[];
    postpartumActivityNutritionBeveragesCaffeine?: string;
    postpartumActivityNutritionMeals?: string;
    postpartumActivityNutritionWater?: string;
    postpartumActivityNutritionBeverages?: string[];
    postpartumActivityNutritionNotes?: string;
  };
  clientId: string;
  practitionerId: string;
  encounterId: string;
  questionnaireId: string;
  status: QuestionnaireResponse["status"];
  questionnaireResponse?: QuestionnaireResponse;
};

type GetPostpartumMedicationAndTherapiesQuestionnaireResponseParams = {
  form: {
    postpartumMedicationAndTherapiesMedicationsFormset?: Array<{
      postpartumMedicationAndTherapiesMedicationsFormsetCode?: string;
      postpartumMedicationAndTherapiesMedicationsFormsetMedication?: Coding;
      postpartumMedicationAndTherapiesMedicationsFormsetForm?: Coding;
      postpartumMedicationAndTherapiesMedicationFormDosage?: {
        postpartumMedicationAndTherapiesMedicationsFormsetDosage?: Coding;
        postpartumMedicationAndTherapiesMedicationsFormsetRoute?: Coding;
      };
      postpartumMedicationAndTherapiesMedicationsFrequencyDuration?: {
        postpartumMedicationAndTherapiesMedicationsFormsetFrequency?: Coding;
        postpartumMedicationAndTherapiesMedicationsFormsetDuration?: Coding;
        postpartumMedicationAndTherapiesMedicationsFrequencyDurationXDays?: number;
      };
      postpartumMedicationAndTherapiesMedicationsFormsetSpecialInstructions?: Coding;
      postpartumMedicationAndTherapiesMedicationsFormsetPRNReason?: Coding;
    }>;
    postpartumMedicationAndTherapiesTherapiesFormset?: Array<{
      postpartumMedicationAndTherapiesTherapiesFormsetCode?: string;
      postpartumMedicationAndTherapiesTherapiesFormsetTherapy?: string;
      postpartumMedicationAndTherapiesTherapiesReason?: Coding;
      postpartumMedicationAndTherapiesTherapiesReasonOther?: string;
    }>;
    postpartumMedicationAndTherapiesNotes?: string;
  };
  clientId: string;
  practitionerId: string;
  encounterId: string;
  questionnaireId: string;
  status: QuestionnaireResponse["status"];
  questionnaireResponse?: QuestionnaireResponse;
};

type GetPostpartumSleepQuestionnaireResponseParams = {
  form: {
    postpartumSleepHours?: number;
    postpartumSleepWakeUp?: string;
    postpartumSleepFallingAsleep?: string;
    postpartumSleepStayingAsleep?: string;
    postpartumSleepBreathing?: string;
    postpartumSleepNotes?: string;
  };
  clientId: string;
  practitionerId: string;
  encounterId: string;
  questionnaireId: string;
  status: QuestionnaireResponse["status"];
  questionnaireResponse?: QuestionnaireResponse;
};

type GetPostpartumSocialHealthQuestionnaireResponseParams = {
  form: {
    postpartumSocialHealthSmoke?: string;
    postpartumSocialHealthSmokeAmount?: string;
    postpartumSocialHealthSmokeFrequency?: string;
    postpartumSocialHealthDrink?: string;
    postpartumSocialHealthDrinkAmount?: string;
    postpartumSocialHealthDrinkFrequency?: string;
    postpartumSocialHealthDrugs?: string;
    postpartumSocialHealthDrugsType?: string;
    postpartumSocialHealthWork?: string;
    postpartumSocialHealthWorkCapacity?: string;
    postpartumSocialHealthWorkFrequency?: string;
    postpartumSocialHealthSupport?: string;
    postpartumSocialHealthSupportBy?: string[];
    postpartumSocialHealthNotes?: string;
  };
  clientId: string;
  practitionerId: string;
  encounterId: string;
  questionnaireId: string;
  status: QuestionnaireResponse["status"];
  questionnaireResponse?: QuestionnaireResponse;
};

type GetPostpartumEmotionalHealthQuestionnaireResponseParams = {
  form: {
    postpartumEmotionalHealthMood?: string[];
    postpartumEmotionalHealthTasks?: string;
    postpartumEmotionalHealthBonding?: string;
    postpartumEmotionalHealthSuicide?: string;
    postpartumEmotionalHealthHallucinations?: string;
    postpartumEmotionalHealthNotes?: string;
  };
  clientId: string;
  practitionerId: string;
  encounterId: string;
  questionnaireId: string;
  status: QuestionnaireResponse["status"];
  questionnaireResponse?: QuestionnaireResponse;
};

type GetPostpartumSexualActivityQuestionnaireResponseParams = {
  form: {
    postpartumSexualActivityResumed?: string;
    postpartumSexualActivityDiscomfort?: string;
    postpartumSexualActivityPain?: string[];
    postpartumSexualActivityDryness?: string;
    postpartumSexualActivityContraception?: string;
    postpartumSexualActivityContraceptionMethod?: string[];
    postpartumSexualActivityIUD?: string;
    postpartumSexualActivityHormoneFree?: string[];
    postpartumSexualActivityProgesteroneContaining?: string[];
    postpartumSexualActivityFuture?: string;
    postpartumSexualActivityFutureWhen?: string;
    postpartumSexualActivityNotes?: string;
  };
  clientId: string;
  practitionerId: string;
  encounterId: string;
  questionnaireId: string;
  status: QuestionnaireResponse["status"];
  questionnaireResponse?: QuestionnaireResponse;
};

type GetPostpartumVitalsQuestionnaireResponseParams = {
  form: PostpartumVitalsFormType;
  clientId: string;
  practitionerId: string;
  encounterId: string;
  questionnaireId: string;
  status: QuestionnaireResponse["status"];
  questionnaireResponse?: QuestionnaireResponse;
};

export const getEncounterQuestionnaireResponse = ({
  form,
  clientId,
  practitionerReference,
  status,
  encounterId,
  questionnaireId,
  newbornPatientReference,
}: GetEncounterQuestionnaireResponseParams) => {
  const questionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    status,
    identifier: { system: Systems.QH, value: Questionnaires.Encounter },
    subject: { reference: joinReferenceString("Patient", clientId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    ...(encounterId
      ? { encounter: { reference: joinReferenceString("Encounter", encounterId) } }
      : {}),
    author: practitionerReference,
    item: [
      {
        linkId: "status",
        text: "Status",
        answer: [{ valueString: form.status ?? "" }],
      },
      {
        linkId: "template",
        text: "Template",
        answer: [{ valueCoding: form.template ?? "" }],
      },
      {
        linkId: "type",
        text: "Type",
        answer: [{ valueCoding: (form.type as Coding) ?? "" }],
      },
      {
        linkId: "location",
        text: "Location",
        answer: [{ valueCoding: (form.location as Coding) ?? "" }],
      },
      {
        linkId: "startDate",
        text: "Start Date",
        answer: [
          {
            valueDateTime:
              combineDateTimeInTimezone(
                form.startDate ?? null,
                form.startTime ?? null,
                form.startDateTimezone?.code,
              ) ??
              combineDateTimeToUTC(form.startDate ?? null, form.startTime ?? null) ??
              form.startDate?.toISOString() ??
              null,
          },
        ],
      },
      {
        linkId: "startDateTimezone",
        text: "Start Date Timezone",
        answer: [{ valueCoding: form.startDateTimezone }],
      },
      ...(form.clinicalJustification?.code
        ? [
            {
              linkId: "clinicalJustification",
              text: REASON_FOR_TRANSFER_LABEL,
              answer: [{ valueCoding: (form.clinicalJustification as Coding) ?? "" }],
            },
          ]
        : []),
      ...(form.transferringFrom?.code
        ? [
            {
              linkId: "transferringFrom",
              text: "Transferring From",
              answer: [{ valueCoding: (form.transferringFrom as Coding) ?? "" }],
            },
          ]
        : []),
      ...(form.episodeOfCare
        ? [
            {
              linkId: "episodeOfCare",
              text: "Episode of Care",
              answer: [
                {
                  valueReference: {
                    reference: joinReferenceString("EpisodeOfCare", form.episodeOfCare),
                  },
                },
              ],
            },
          ]
        : []),
    ],
  };

  for (const physicalExam of form.physicalExams ?? []) {
    questionnaireResponse.item?.push({
      linkId: "physicalExam",
      text: "Physical Exam",
      answer: [{ valueUri: physicalExam }],
    });
  }

  if (newbornPatientReference) {
    questionnaireResponse.extension = setExtensionValue(
      questionnaireResponse.extension,
      Extensions.NewbornPatientReference,
      { valueReference: newbornPatientReference },
    );
  }

  return questionnaireResponse;
};

export const getEncounterAnswersQuestionnaireResponse = ({
  form,
  clientId,
  practitionerReference,
  status,
  questionnaire,
  encounterId,
  questionnaireResponse,
}: GetEncounterAnswersQuestionnaireResponseParams) => {
  const isAmendmentForm = AMENDMENT_QUESTIONNAIRE_FIELD in form;

  const questionnaireResponseUpdated: QuestionnaireResponse = {
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    resourceType: "QuestionnaireResponse",
    status,
    identifier: { system: Systems.QH, value: questionnaire.url },
    subject: { reference: joinReferenceString("Patient", clientId) },
    author: practitionerReference,
    questionnaire: joinReferenceString("Questionnaire", questionnaire.id!),
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    item: [
      ...(form.encounterNote
        ? [
            {
              linkId: "encounterNote",
              text: "Encounter Note",
              answer: [{ valueString: form.encounterNote }],
            },
          ]
        : []),
      ...(form.encounterClientSummary
        ? [
            {
              linkId: "encounterClientSummary",
              text: "Encounter Client Summary",
              answer: [{ valueString: form.encounterClientSummary }],
            },
          ]
        : []),
      ...getVitalsSignItems(form.encounterVitalSigns),
      ...getPrenatalFlowsheetItems(form.encounterFlowSheet),
      ...(isAmendmentForm ? getEncounterDetailsItems(form.encounterDetails) : []),
      ...(isAmendmentForm ? getEncounterAmendmentItems(form.encounterAmendmentInfo) : []),
      ...getEncounterDiagnosesItems(form.encounterClientDiagnoses),
    ],
  };

  return questionnaireResponseUpdated;
};

const getVitalsSignItems = (form?: VitalSignsFormType): QuestionnaireResponseItem[] => {
  if (!form) return [];

  // Check if there are any meaningful vital sign values (not just default units)
  const hasVitalSignValues =
    form.encounterVitalSignsWeight !== undefined ||
    form.encounterVitalSignsHeightFeet !== undefined ||
    form.encounterVitalSignsHeightInches !== undefined ||
    form.encounterVitalSignsHeightCm !== undefined ||
    form.encounterVitalSignsHeartRate !== undefined ||
    form.encounterVitalSignsTemperature !== undefined ||
    form.encounterVitalSignsRespiratoryRate !== undefined ||
    form.encounterVitalSignsSystolicBP !== undefined ||
    form.encounterVitalSignsDiastolicBP !== undefined ||
    form.encounterVitalSignsVitalsComments !== undefined;

  if (!hasVitalSignValues) return [];

  const items: QuestionnaireResponseItem[] = [];

  if (form.encounterVitalSignsWeight !== undefined) {
    items.push({
      linkId: "encounterVitalSignsWeight",
      text: "Weight",
      answer: [{ valueDecimal: form.encounterVitalSignsWeight }],
    });
  }

  if (form.encounterVitalSignsWeightUnit) {
    items.push({
      linkId: "encounterVitalSignsWeightUnit",
      text: "Weight Unit",
      answer: [{ valueString: form.encounterVitalSignsWeightUnit }],
    });
  }

  if (form.encounterVitalSignsHeightUnit) {
    items.push({
      linkId: "encounterVitalSignsHeightUnit",
      text: "Height Unit",
      answer: [{ valueString: form.encounterVitalSignsHeightUnit }],
    });
  }

  if (form.encounterVitalSignsHeightFeet !== undefined) {
    items.push({
      linkId: "encounterVitalSignsHeightFeet",
      text: "Height (Feet)",
      answer: [{ valueDecimal: form.encounterVitalSignsHeightFeet }],
    });
  }

  if (form.encounterVitalSignsHeightInches !== undefined) {
    items.push({
      linkId: "encounterVitalSignsHeightInches",
      text: "Height (Inches)",
      answer: [{ valueDecimal: form.encounterVitalSignsHeightInches }],
    });
  }

  if (form.encounterVitalSignsHeightCm !== undefined) {
    items.push({
      linkId: "encounterVitalSignsHeightCm",
      text: "Height (cm)",
      answer: [{ valueDecimal: form.encounterVitalSignsHeightCm }],
    });
  }

  if (form.encounterVitalSignsHeartRate !== undefined) {
    items.push({
      linkId: "encounterVitalSignsHeartRate",
      text: "Heart Rate",
      answer: [{ valueDecimal: form.encounterVitalSignsHeartRate }],
    });
  }

  if (form.encounterVitalSignsTemperature !== undefined) {
    items.push({
      linkId: "encounterVitalSignsTemperature",
      text: "Temperature",
      answer: [{ valueDecimal: form.encounterVitalSignsTemperature }],
    });
  }

  if (form.encounterVitalSignsTemperatureUnit) {
    items.push({
      linkId: "encounterVitalSignsTemperatureUnit",
      text: "Temperature Unit",
      answer: [{ valueString: form.encounterVitalSignsTemperatureUnit }],
    });
  }

  if (form.encounterVitalSignsTemperatureMethod) {
    items.push({
      linkId: "encounterVitalSignsTemperatureMethod",
      text: "Temperature Method",
      answer: [{ valueCoding: form.encounterVitalSignsTemperatureMethod }],
    });
  }

  if (form.encounterVitalSignsRespiratoryRate !== undefined) {
    items.push({
      linkId: "encounterVitalSignsRespiratoryRate",
      text: "Respiratory Rate",
      answer: [{ valueDecimal: form.encounterVitalSignsRespiratoryRate }],
    });
  }

  if (form.encounterVitalSignsSystolicBP !== undefined) {
    items.push({
      linkId: "encounterVitalSignsSystolicBP",
      text: "Systolic Blood Pressure",
      answer: [{ valueDecimal: form.encounterVitalSignsSystolicBP }],
    });
  }

  if (form.encounterVitalSignsDiastolicBP !== undefined) {
    items.push({
      linkId: "encounterVitalSignsDiastolicBP",
      text: "Diastolic Blood Pressure",
      answer: [{ valueDecimal: form.encounterVitalSignsDiastolicBP }],
    });
  }

  if (form.encounterVitalSignsVitalsComments) {
    items.push({
      linkId: "encounterVitalSignsVitalsComments",
      text: "Comments",
      answer: [{ valueString: form.encounterVitalSignsVitalsComments }],
    });
  }

  return [
    {
      linkId: "encounterVitalSigns",
      text: "Vital Signs",
      item: items,
    },
  ];
};

const getPrenatalFlowsheetItems = (
  form?: PrenatalFlowsheetFormType,
): QuestionnaireResponseItem[] => {
  if (!form || !Object.values(form).some((value) => value)) return [];

  const items: QuestionnaireResponseItem[] = [];

  if (form.encounterFlowSheetWeight !== undefined) {
    items.push({
      linkId: "encounterFlowSheetWeight",
      text: "Weight",
      answer: [{ valueDecimal: form.encounterFlowSheetWeight }],
    });
  }

  if (form.encounterFlowSheetWeightUnit) {
    items.push({
      linkId: "encounterFlowSheetWeightUnit",
      text: "Weight Unit",
      answer: [{ valueString: form.encounterFlowSheetWeightUnit }],
    });
  }

  if (form.encounterFlowSheetSystolicBP !== undefined) {
    items.push({
      linkId: "encounterFlowSheetSystolicBP",
      text: "Systolic Blood Pressure",
      answer: [{ valueDecimal: form.encounterFlowSheetSystolicBP }],
    });
  }

  if (form.encounterFlowSheetDiastolicBP !== undefined) {
    items.push({
      linkId: "encounterFlowSheetDiastolicBP",
      text: "Diastolic Blood Pressure",
      answer: [{ valueDecimal: form.encounterFlowSheetDiastolicBP }],
    });
  }

  if (form.encounterFlowSheetHeartRate !== undefined) {
    items.push({
      linkId: "encounterFlowSheetHeartRate",
      text: "Heart Rate",
      answer: [{ valueDecimal: form.encounterFlowSheetHeartRate }],
    });
  }

  if (form.encounterFlowSheetFundalHeight !== undefined) {
    items.push({
      linkId: "encounterFlowSheetFundalHeight",
      text: "Fundal Height",
      answer: [{ valueDecimal: form.encounterFlowSheetFundalHeight }],
    });
  }

  if (form.encounterFlowSheetFundalHeightUnit) {
    items.push({
      linkId: "encounterFlowSheetFundalHeightUnit",
      text: "Fundal Height Unit",
      answer: [{ valueString: form.encounterFlowSheetFundalHeightUnit }],
    });
  }

  if (form.encounterFlowSheetFetalPresentation) {
    items.push({
      linkId: "encounterFlowSheetFetalPresentation",
      text: "Fetal Presentation",
      answer: [{ valueString: form.encounterFlowSheetFetalPresentation }],
    });
  }

  if (form.encounterFlowSheetFetalHeartRate !== undefined) {
    items.push({
      linkId: "encounterFlowSheetFetalHeartRate",
      text: "Fetal Heart Rate",
      answer: [{ valueDecimal: form.encounterFlowSheetFetalHeartRate }],
    });
  }

  if (form.encounterFlowSheetFetalMovement) {
    items.push({
      linkId: "encounterFlowSheetFetalMovement",
      text: "Fetal Movement",
      answer: [{ valueCoding: form.encounterFlowSheetFetalMovement }],
    });
  }

  if (form.encounterFlowSheetUrineProtein) {
    items.push({
      linkId: "encounterFlowSheetUrineProtein",
      text: "Urine Protein",
      answer: [{ valueCoding: form.encounterFlowSheetUrineProtein }],
    });
  }

  if (form.encounterFlowSheetUrineGlucose) {
    items.push({
      linkId: "encounterFlowSheetUrineGlucose",
      text: "Urine Glucose",
      answer: [{ valueCoding: form.encounterFlowSheetUrineGlucose }],
    });
  }

  if (form.encounterFlowSheetEdema) {
    items.push({
      linkId: "encounterFlowSheetEdema",
      text: "Edema",
      answer: [{ valueCoding: form.encounterFlowSheetEdema }],
    });
  }

  if (form.encounterFlowSheetNotes) {
    items.push({
      linkId: "encounterFlowSheetNotes",
      text: "Notes",
      answer: [{ valueString: form.encounterFlowSheetNotes }],
    });
  }

  if (form.encounterFlowSheetDate) {
    items.push({
      linkId: "encounterFlowSheetDate",
      text: "Date",
      answer: [{ valueDateTime: form.encounterFlowSheetDate.toISOString() }],
    });
  }

  return [
    {
      linkId: "encounterFlowSheet",
      text: "Prenatal Flow Sheet",
      item: items,
    },
  ];
};

const getEncounterDetailsItems = (form?: EncounterDetailsFormType): QuestionnaireResponseItem[] => {
  if (!form || !Object.values(form).some((value) => value)) return [];

  const items: QuestionnaireResponseItem[] = [];

  if (form.encounterTemplate) {
    items.push({
      linkId: "encounterTemplate",
      text: "Template",
      answer: [{ valueCoding: form.encounterTemplate }],
    });
  }

  if (form.encounterType) {
    items.push({
      linkId: "encounterType",
      text: "Type",
      answer: [{ valueCoding: form.encounterType }],
    });
  }

  if (form.encounterLocation) {
    items.push({
      linkId: "encounterLocation",
      text: "Location",
      answer: [{ valueCoding: form.encounterLocation }],
    });
  }

  items.push(...getEncounterServiceDateDetailQuestionnaireItems(form));

  return [
    {
      linkId: "encounterDetails",
      text: "Encounter Details",
      item: items,
    },
  ];
};

const getEncounterAmendmentItems = (
  form?: EncounterAmendmentInfoValuesType,
): QuestionnaireResponseItem[] => {
  if (!form || !Object.values(form).some((value) => value)) return [];

  const items: QuestionnaireResponseItem[] = [];

  if (form.encounterAmendmentInfoReason) {
    items.push({
      linkId: "encounterAmendmentInfoReason",
      text: "Amendment Reason",
      answer: [
        typeof form.encounterAmendmentInfoReason === "string"
          ? { valueString: form.encounterAmendmentInfoReason }
          : { valueCoding: form.encounterAmendmentInfoReason },
      ],
    });
  }

  if (form.encounterAmendmentInfoSpecify) {
    items.push({
      linkId: "encounterAmendmentInfoSpecify",
      text: "Specify",
      answer: [{ valueString: form.encounterAmendmentInfoSpecify }],
    });
  }

  if (form.encounterAmendmentInfoNotes) {
    items.push({
      linkId: "encounterAmendmentInfoNotes",
      text: "Notes",
      answer: [{ valueString: form.encounterAmendmentInfoNotes }],
    });
  }

  if (form.encounterAmendmentInfoNotifyClient !== undefined) {
    items.push({
      linkId: "encounterAmendmentInfoNotifyClient",
      text: "Notify Client",
      answer: [{ valueBoolean: form.encounterAmendmentInfoNotifyClient }],
    });
  }

  return [
    {
      linkId: "encounterAmendmentInfo",
      text: "Amendment Information",
      item: items,
    },
  ];
};

/**
 * Extracts encounter diagnoses information from the provided form and formats it as QuestionnaireResponseItems.
 * Returns an array of QuestionnaireResponseItems representing encounter diagnoses, or an empty array if no diagnoses information is provided.
 * @param form - An array of EncounterDiagnosesFormType objects containing diagnosis details.
 * @returns An array of QuestionnaireResponseItems.
 */
export const getEncounterDiagnosesItems = (
  form?: EncounterDiagnosesFormType[],
): QuestionnaireResponseItem[] => {
  if (!form || !Object.values(form).some((value) => value)) return [];

  const items: QuestionnaireResponseItem[] = [];

  form.forEach((dxPlan: EncounterDiagnosesFormType) => {
    items.push({
      linkId: "encounterClientDiagnosis",
      text: "Diagnosis",
      item: [
        {
          linkId: "encounterClientDiagnosisCode",
          text: "Code",
          answer: [{ valueCoding: dxPlan.encounterDiagnosisCode }],
        },
        {
          linkId: "encounterClientDiagnosisAddToProblemList",
          text: "Add to Problem List",
          answer: [{ valueBoolean: dxPlan.encounterDiagnosisAddToProblemList }],
        },
        ...(dxPlan.encounterDiagnosisNotes
          ? [
              {
                linkId: "encounterClientDiagnosisNotes",
                text: "Notes",
                answer: [{ valueString: dxPlan.encounterDiagnosisNotes }],
              },
            ]
          : []),
      ],
    });
  });

  return [
    {
      linkId: "encounterClientDiagnoses",
      text: "Diagnoses",
      item: items,
    },
  ];
};

export const getNewbornEncounterBirthDetailsQuestionnaireResponse = ({
  clientId,
  form,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetNewbornEncounterQuestionnaireResponseParams) => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      ...(form.sex
        ? [
            {
              linkId: "birthDetailsSex",
              text: "Sex",
              answer: [{ valueString: form.sex }],
            },
          ]
        : []),
      ...(form.sexSpecify
        ? [
            {
              linkId: "birthDetailsSexSpecify",
              text: "Specify",
              answer: [{ valueString: form.sexSpecify }],
            },
          ]
        : []),
      ...(form.babyName
        ? [
            {
              linkId: "birthDetailsBabyName",
              text: "Baby's Name",
              answer: [{ valueString: form.babyName }],
            },
          ]
        : []),
    ],
  };

  return updatedQuestionnaireResponse;
};

export const getNewbornEncounterSizeQuestionnaireResponse = ({
  clientId,
  form,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetNewbornEncounterSizeQuestionnaireResponseParams) => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      ...(form.scale
        ? [
            {
              linkId: "sizeScale",
              text: "Scale",
              answer: [{ valueString: form.scale }],
            },
          ]
        : []),
      ...(form.weight
        ? [
            {
              linkId: "sizeWeight",
              text: "Weight",
              answer: [{ valueDecimal: form.weight }],
            },
          ]
        : []),
      ...(form.oz !== undefined
        ? [
            {
              linkId: "sizeWeightOunces",
              text: "Weight Ounces",
              answer: [{ valueDecimal: form.oz }],
            },
          ]
        : []),
      ...(form.length
        ? [
            {
              linkId: "sizeLength",
              text: "Length",
              answer: [{ valueDecimal: form.length }],
            },
          ]
        : []),
      ...(form.headCircumference
        ? [
            {
              linkId: "sizeHeadCircumference",
              text: "Head Circumference",
              answer: [{ valueDecimal: form.headCircumference }],
            },
          ]
        : []),
      ...(form.chestCircumference
        ? [
            {
              linkId: "sizeChestCircumference",
              text: "Chest Circumference",
              answer: [{ valueDecimal: form.chestCircumference }],
            },
          ]
        : []),
      ...(form.notes
        ? [
            {
              linkId: "sizeNotes",
              text: "Notes",
              answer: [{ valueString: form.notes }],
            },
          ]
        : []),
    ],
  };

  return updatedQuestionnaireResponse;
};

export const getNewbornIPPEncounterVitalsSignQuestionnaireResponse = ({
  clientId,
  form,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetNewbornEncounterVitalsSignQuestionnaireResponseParams) => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      ...(form.heartRate != null
        ? [
            {
              linkId: "newbornIPPVitalsSignHeartRate",
              text: "Heart Rate",
              answer: [{ valueInteger: form.heartRate }],
            },
          ]
        : []),
      ...(form.respiratoryRate != null
        ? [
            {
              linkId: "newbornIPPVitalsSignRespiratoryRate",
              text: "Respiratory Rate",
              answer: [{ valueInteger: form.respiratoryRate }],
            },
          ]
        : []),
      ...(form.temperature != null
        ? [
            {
              linkId: "newbornIPPVitalsSignTemperature",
              text: "Temperature",
              answer: [{ valueDecimal: form.temperature }],
            },
          ]
        : []),
      ...(form.temperature && form.temperatureUnit
        ? [
            {
              linkId: "newbornIPPVitalsSignTemperatureUnit",
              text: "Temperature Unit",
              answer: [{ valueString: form.temperatureUnit }],
            },
          ]
        : []),
      ...(form.temperatureMethod?.code
        ? [
            {
              linkId: "newbornIPPVitalsSignTemperatureMethod",
              text: "Temperature Method",
              answer: [
                {
                  valueCoding: form.temperatureMethod,
                },
              ],
            },
          ]
        : []),
      ...(form.color
        ? [
            {
              linkId: "newbornIPPVitalsSignColor",
              text: "Color",
              answer: [{ valueCoding: form.color }],
            },
          ]
        : []),
      ...(form.void
        ? [
            {
              linkId: "newbornIPPVitalsSignVoid",
              text: "Void",
              answer: [{ valueString: form.void }],
            },
          ]
        : []),
      ...(form.stool
        ? [
            {
              linkId: "newbornIPPVitalsSignStool",
              text: "Stool",
              answer: [{ valueString: form.stool }],
            },
          ]
        : []),
      ...(form.notes
        ? [
            {
              linkId: "newbornIPPVitalsSignNotes",
              text: "Notes",
              answer: [{ valueString: form.notes }],
            },
          ]
        : []),
    ],
  };

  return updatedQuestionnaireResponse;
};

export const getNewbornIPPEncounterFeedingQuestionnaireResponse = ({
  clientId,
  form,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetNewbornIPPEncounterFeedingQuestionnaireResponseParams) => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      ...(typeof form.nursing === "boolean"
        ? [
            {
              linkId: "nursing",
              text: "Nursing",
              answer: [{ valueBoolean: form.nursing }],
            },
          ]
        : []),
      ...(typeof form.formula === "boolean"
        ? [
            {
              linkId: "formula",
              text: "Formula",
              answer: [{ valueBoolean: form.formula }],
            },
          ]
        : []),
      ...(form.notes
        ? [
            {
              linkId: "notes",
              text: "Notes",
              answer: [{ valueString: form.notes }],
            },
          ]
        : []),
    ],
  };

  return updatedQuestionnaireResponse;
};

export const getNewbornIPPMedicationAndTherapiesQuestionnaireResponse = ({
  clientId,
  form,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetNewbornIPPMedicationAndTherapiesQuestionnaireResponseParams) => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      ...(form.vitaminK
        ? [
            {
              linkId: "vitaminK",
              text: "Vitamin K",
              answer: [{ valueString: form.vitaminK }],
            },
          ]
        : []),
      ...(form.vitaminKDose
        ? [
            {
              linkId: "vitaminKDose",
              text: "Dose",
              answer: [{ valueString: form.vitaminKDose }],
            },
          ]
        : []),
      ...(form.vitaminKLotNumber
        ? [
            {
              linkId: "vitaminKLotNumber",
              text: "Lot Number",
              answer: [{ valueString: form.vitaminKLotNumber }],
            },
          ]
        : []),
      ...(form.vitaminKNotes
        ? [
            {
              linkId: "vitaminKNotes",
              text: "Notes",
              answer: [{ valueString: form.vitaminKNotes }],
            },
          ]
        : []),
      ...(form.eyeProphylaxis
        ? [
            {
              linkId: "eyeProphylaxis",
              text: "Eye Prophylaxis",
              answer: [{ valueString: form.eyeProphylaxis }],
            },
          ]
        : []),
      ...(form.eyeProphylaxisNotes
        ? [
            {
              linkId: "eyeProphylaxisNotes",
              text: "Notes",
              answer: [{ valueString: form.eyeProphylaxisNotes }],
            },
          ]
        : []),
      ...(form.hepatitisB
        ? [
            {
              linkId: "hepatitisB",
              text: "Hepatitis B Vaccine",
              answer: [{ valueString: form.hepatitisB }],
            },
          ]
        : []),
      ...(form.hepatitisBNotes
        ? [
            {
              linkId: "hepatitisBNotes",
              text: "Notes",
              answer: [{ valueString: form.hepatitisBNotes }],
            },
          ]
        : []),
    ],
  };

  return updatedQuestionnaireResponse;
};

export const getNewbornIPPGestationalAgeQuestionnaireResponse = ({
  clientId,
  form,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
  lmpGestationalAge,
}: GetNewbornIPPGestationalAgeQuestionnaireResponseParams) => {
  const items: QuestionnaireResponse["item"] = [];

  // Add LMP gestational age if provided (for audit trail)
  // Use valueDecimal to preserve the exact LMP value (e.g., 18.58 weeks)
  if (lmpGestationalAge !== undefined) {
    items.push({
      linkId: "gestationalAgeFromLMP",
      text: "Gestational Age (from LMP)",
      answer: [{ valueDecimal: lmpGestationalAge }],
    });
  }

  if (form.gestationalAge !== undefined) {
    items.push({
      linkId: "gestationalAge",
      text: "Gestational Age",
      answer: [{ valueInteger: form.gestationalAge }],
    });
  }

  if (form.gestationalAgeBallardScore !== undefined) {
    items.push({
      linkId: "gestationalAgeBallardScore",
      text: "Ballard Score",
      answer: [{ valueInteger: form.gestationalAgeBallardScore }],
    });
  }

  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: items,
  };

  return updatedQuestionnaireResponse;
};

export const getNewbornIPPEncounterQuestionnaireResponse = (
  form: NewbornIPPAmendmentEncounterFormType,
  status: QuestionnaireResponse["status"],
  patientId: string,
  encounterId: string,
  questionnaire: Questionnaire,
  previousQuestionnaireResponse?: QuestionnaireResponse,
) => {
  const questionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(previousQuestionnaireResponse
      ? {
          id: previousQuestionnaireResponse.id,
        }
      : {}),
    status,
    subject: { reference: joinReferenceString("Patient", patientId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaire.id!),
    authored: new Date().toISOString(),
    item: [
      ...(form.encounterDetails?.encounterTemplate
        ? [
            {
              linkId: "encounterTemplate",
              answer: [{ valueCoding: form.encounterDetails.encounterTemplate }],
            },
          ]
        : []),
      ...(form.encounterDetails?.encounterType
        ? [
            {
              linkId: "encounterType",
              answer: [{ valueCoding: form.encounterDetails.encounterType }],
            },
          ]
        : []),
      ...(form.encounterDetails?.encounterLocation
        ? [
            {
              linkId: "encounterLocation",
              answer: [{ valueCoding: form.encounterDetails.encounterLocation }],
            },
          ]
        : []),
      ...getEncounterServiceDateDetailQuestionnaireItems(form.encounterDetails),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoReason
        ? [
            {
              linkId: "encounterAmendmentInfoReason",
              answer: [
                typeof form.encounterAmendmentInfo.encounterAmendmentInfoReason === "string"
                  ? { valueString: form.encounterAmendmentInfo.encounterAmendmentInfoReason }
                  : { valueCoding: form.encounterAmendmentInfo.encounterAmendmentInfoReason },
              ],
            },
          ]
        : []),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoSpecify
        ? [
            {
              linkId: "encounterAmendmentInfoSpecify",
              answer: [{ valueString: form.encounterAmendmentInfo.encounterAmendmentInfoSpecify }],
            },
          ]
        : []),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoNotes
        ? [
            {
              linkId: "encounterAmendmentInfoNotes",
              answer: [{ valueString: form.encounterAmendmentInfo.encounterAmendmentInfoNotes }],
            },
          ]
        : []),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoNotifyClient
        ? [
            {
              linkId: "encounterAmendmentInfoNotifyClient",
              answer: [
                { valueBoolean: form.encounterAmendmentInfo.encounterAmendmentInfoNotifyClient },
              ],
            },
          ]
        : []),
      ...(form.notes
        ? [
            {
              linkId: "notes",
              answer: [
                {
                  valueString: form.notes,
                },
              ],
            },
          ]
        : []),
      ...(form.encounterClientSummary
        ? [
            {
              linkId: "encounterClientSummary",
              text: "Encounter Client Summary",
              answer: [{ valueString: form.encounterClientSummary }],
            },
          ]
        : []),
      ...getEncounterDiagnosesItems(form.encounterClientDiagnoses),
    ],
  } as QuestionnaireResponse;

  return questionnaireResponse;
};

export const getNewbornPostpartumEncounterSizeQuestionnaireResponse = ({
  clientId,
  form,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetNewbornPostpartumEncounterSizeQuestionnaireResponseParams) => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      ...(form.scale
        ? [
            {
              linkId: "sizeScale",
              text: "Scale",
              answer: [{ valueString: form.scale }],
            },
          ]
        : []),
      ...(form.weight
        ? [
            {
              linkId: "sizeWeight",
              text: "Weight",
              answer: [{ valueDecimal: form.weight }],
            },
          ]
        : []),
      ...(form.weightOunces !== undefined && form.weightOunces !== null
        ? [
            {
              linkId: "sizeWeightOunces",
              answer: [{ valueDecimal: form.weightOunces }],
            },
          ]
        : []),
      ...(form.length
        ? [
            {
              linkId: "sizeLength",
              text: "Length",
              answer: [{ valueDecimal: form.length }],
            },
          ]
        : []),
      ...(form.headCircumference
        ? [
            {
              linkId: "sizeHeadCircumference",
              text: "Head Circumference",
              answer: [{ valueDecimal: form.headCircumference }],
            },
          ]
        : []),
      ...(form.chestCircumference
        ? [
            {
              linkId: "sizeChestCircumference",
              text: "Chest Circumference",
              answer: [{ valueDecimal: form.chestCircumference }],
            },
          ]
        : []),
      ...(form.notes
        ? [
            {
              linkId: "sizeNotes",
              text: "Notes",
              answer: [{ valueString: form.notes }],
            },
          ]
        : []),
    ],
  };

  return updatedQuestionnaireResponse;
};

export const getNewbornPostpartumEncounterVitalsSignQuestionnaireResponse = ({
  clientId,
  form,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetNewbornPostpartumEncounterVitalsSignQuestionnaireResponseParams) => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      ...(form.heartRate != null
        ? [
            {
              linkId: "vitalsSignHeartRate",
              text: "Heart Rate",
              answer: [{ valueInteger: form.heartRate }],
            },
          ]
        : []),
      ...(form.respiratoryRate != null
        ? [
            {
              linkId: "vitalsSignRespiratoryRate",
              text: "Respiratory Rate",
              answer: [{ valueInteger: form.respiratoryRate }],
            },
          ]
        : []),
      ...(form.temperature != null
        ? [
            {
              linkId: "vitalsSignTemperature",
              text: "Temperature",
              answer: [{ valueDecimal: form.temperature }],
            },
          ]
        : []),
      ...(form.temperatureUnit
        ? [
            {
              linkId: "vitalsSignTemperatureUnit",
              text: "Temperature Unit",
              answer: [{ valueString: form.temperatureUnit }],
            },
          ]
        : []),
      ...(form.temperatureMethod?.code
        ? [
            {
              linkId: "vitalsSignTemperatureMethod",
              text: "Temperature Method",
              answer: [
                {
                  valueCoding: form.temperatureMethod,
                },
              ],
            },
          ]
        : []),
      ...(form.color
        ? [
            {
              linkId: "vitalsSignColor",
              text: "Color",
              answer: [{ valueCoding: form.color }],
            },
          ]
        : []),
      ...(form.jaundice
        ? [
            {
              linkId: "vitalsSignJaundice",
              text: "Jaundice",
              answer: [{ valueCoding: form.jaundice }],
            },
          ]
        : []),
      ...(form.void
        ? [
            {
              linkId: "vitalsSignVoid",
              text: "Void",
              answer: [{ valueString: form.void }],
            },
          ]
        : []),
      ...(form.stool
        ? [
            {
              linkId: "vitalsSignStool",
              text: "Stool",
              answer: [{ valueString: form.stool }],
            },
          ]
        : []),
      ...(form.notes
        ? [
            {
              linkId: "vitalsSignNotes",
              text: "Notes",
              answer: [{ valueString: form.notes }],
            },
          ]
        : []),
    ],
  };

  return updatedQuestionnaireResponse;
};

export const getNewbornPostpartumMedicationAndTherapiesQuestionnaireResponse = ({
  clientId,
  form,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetNewbornPostpartumMedicationAndTherapiesQuestionnaireResponseParams) => {
  const medications = Object.values(form.newbornPostpartumMedicationFormset || []);
  const therapies = Object.values(form.newbornPostpartumTherapiesFormset || []);

  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      {
        linkId: "newbornPostpartumMedsAndTherapies",
        text: "Meds & Therapies",
        item: [
          ...medications.map((option) => ({
            linkId: "newbornPostpartumMedicationFormset",
            text: "Medication / Vaccine",
            item: [
              {
                linkId: "newbornPostpartumMedicationFormsetCode",
                answer: [{ valueString: option.newbornPostpartumMedicationFormsetCode }],
              },
              {
                linkId: "newbornPostpartumMedicationFormsetTitle",
                text: "Medication/Vaccine Name",
                answer: option.newbornPostpartumMedicationFormsetTitle
                  ? [{ valueString: option.newbornPostpartumMedicationFormsetTitle }]
                  : [],
              },
              {
                linkId: "newbornPostpartumMedicationFormsetDoseAndRoute",
                item: [
                  {
                    linkId: "newbornPostpartumMedicationFormsetDose",
                    text: "Dose",
                    answer: option.newbornPostpartumMedicationFormsetDoseAndRoute
                      .newbornPostpartumMedicationFormsetDose
                      ? [
                          {
                            valueString:
                              option.newbornPostpartumMedicationFormsetDoseAndRoute
                                .newbornPostpartumMedicationFormsetDose,
                          },
                        ]
                      : [],
                  },
                  {
                    linkId: "newbornPostpartumMedicationFormsetRoute",
                    text: "Route",
                    answer: option.newbornPostpartumMedicationFormsetDoseAndRoute
                      .newbornPostpartumMedicationFormsetRoute
                      ? [
                          {
                            valueCoding:
                              option.newbornPostpartumMedicationFormsetDoseAndRoute
                                .newbornPostpartumMedicationFormsetRoute,
                          },
                        ]
                      : [],
                  },
                ],
              },
              {
                linkId: "newbornPostpartumMedicationFormsetExpirationDate",
                text: "Expiration Date",
                answer: option.newbornPostpartumMedicationFormsetExpirationDate
                  ? [
                      {
                        valueDate: format(
                          option.newbornPostpartumMedicationFormsetExpirationDate.toISOString() ??
                            "",
                          "yyyy-MM-dd",
                        ),
                      },
                    ]
                  : [],
              },
              {
                linkId: "newbornPostpartumMedicationFormsetLotNumber",
                text: "Lot#",
                answer: option.newbornPostpartumMedicationFormsetLotNumber
                  ? [{ valueString: option.newbornPostpartumMedicationFormsetLotNumber }]
                  : [],
              },
              {
                linkId: "newbornPostpartumMedicationFormsetNotes",
                text: "Notes",
                answer: option.newbornPostpartumMedicationFormsetNotes
                  ? [{ valueString: option.newbornPostpartumMedicationFormsetNotes }]
                  : [],
              },
            ],
          })),

          ...therapies.map((option) => ({
            linkId: "newbornPostpartumTherapiesFormset",
            text: "Therapy",
            item: [
              {
                linkId: "newbornPostpartumTherapiesFormsetCode",
                answer: option.newbornPostpartumTherapiesFormsetCode
                  ? [{ valueString: option.newbornPostpartumTherapiesFormsetCode }]
                  : [],
              },
              {
                linkId: "newbornPostpartumTherapiesFormsetName",
                text: "Name",
                answer: option.newbornPostpartumTherapiesFormsetName
                  ? [{ valueString: option.newbornPostpartumTherapiesFormsetName }]
                  : [],
              },
              {
                linkId: "newbornPostpartumTherapiesFormsetNotes",
                text: "Notes",
                answer: option.newbornPostpartumTherapiesFormsetNotes
                  ? [{ valueString: option.newbornPostpartumTherapiesFormsetNotes }]
                  : [],
              },
            ],
          })),
          ...(form.newbornPostpartumMedsAndTherapiesGeneralNotes
            ? [
                {
                  linkId: "newbornPostpartumMedsAndTherapiesGeneralNotes",
                  text: "Notes",
                  answer: [{ valueString: form.newbornPostpartumMedsAndTherapiesGeneralNotes }],
                },
              ]
            : []),
        ],
      },
    ],
  };

  return updatedQuestionnaireResponse;
};

export const getNewbornPostpartumEncounterQuestionnaireResponse = (
  form: NewbornPostpartumAmendmentEncounterFormType,
  status: QuestionnaireResponse["status"],
  patientId: string,
  encounterId: string,
  practitionerId: string,
  questionnaire: Questionnaire,
  previousQuestionnaireResponse?: QuestionnaireResponse,
) => {
  const questionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(previousQuestionnaireResponse
      ? {
          id: previousQuestionnaireResponse.id,
        }
      : {}),
    status,
    subject: { reference: joinReferenceString("Patient", patientId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaire.id!),
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    authored: new Date().toISOString(),
    item: [
      ...(form.encounterDetails?.encounterTemplate
        ? [
            {
              linkId: "encounterTemplate",
              answer: [{ valueCoding: form.encounterDetails.encounterTemplate }],
            },
          ]
        : []),
      ...(form.encounterDetails?.encounterType
        ? [
            {
              linkId: "encounterType",
              answer: [{ valueCoding: form.encounterDetails.encounterType }],
            },
          ]
        : []),
      ...(form.encounterDetails?.encounterLocation
        ? [
            {
              linkId: "encounterLocation",
              answer: [{ valueCoding: form.encounterDetails.encounterLocation }],
            },
          ]
        : []),
      ...getEncounterServiceDateDetailQuestionnaireItems(form.encounterDetails),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoReason
        ? [
            {
              linkId: "encounterAmendmentInfoReason",
              answer: [
                typeof form.encounterAmendmentInfo.encounterAmendmentInfoReason === "string"
                  ? { valueString: form.encounterAmendmentInfo.encounterAmendmentInfoReason }
                  : { valueCoding: form.encounterAmendmentInfo.encounterAmendmentInfoReason },
              ],
            },
          ]
        : []),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoSpecify
        ? [
            {
              linkId: "encounterAmendmentInfoSpecify",
              answer: [{ valueString: form.encounterAmendmentInfo.encounterAmendmentInfoSpecify }],
            },
          ]
        : []),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoNotes
        ? [
            {
              linkId: "encounterAmendmentInfoNotes",
              answer: [{ valueString: form.encounterAmendmentInfo.encounterAmendmentInfoNotes }],
            },
          ]
        : []),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoNotifyClient
        ? [
            {
              linkId: "encounterAmendmentInfoNotifyClient",
              answer: [
                { valueBoolean: form.encounterAmendmentInfo.encounterAmendmentInfoNotifyClient },
              ],
            },
          ]
        : []),
      ...(form.notes
        ? [
            {
              linkId: "notes",
              answer: [
                {
                  valueString: form.notes,
                },
              ],
            },
          ]
        : []),
      ...(form.encounterClientSummary
        ? [
            {
              linkId: "encounterClientSummary",
              answer: [
                {
                  valueString: form.encounterClientSummary,
                },
              ],
            },
          ]
        : []),
      ...getEncounterDiagnosesItems(form.encounterClientDiagnoses),
    ],
  } as QuestionnaireResponse;

  return questionnaireResponse;
};

const getCodingFormat = (
  optionsList: ValueSetExpansionContains[],
  selectedFormValues: string[],
): Coding[] => {
  return selectedFormValues?.map((codeValue) => {
    return optionsList?.find((coding) => coding.code === codeValue) as Coding;
  });
};

export const getNewbornPostpartumEncounterStateOfNewbornQuestionnaireResponse = ({
  clientId,
  form,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
  newbornStatesList,
  sexOptionsList,
}: GetNewbornPostpartumStateOfNewbornEncounterQuestionnaireResponseParams) => {
  const sexCodingFormat = getCodingFormat(sexOptionsList, [form.sex!]);
  const stateCodingFormat = getCodingFormat(newbornStatesList, form.stateOfNewborn);

  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      ...(form.sex
        ? [
            {
              linkId: "stateOfNewbornSex",
              text: "Sex",
              answer: [{ valueCoding: sexCodingFormat[0] }],
            },
          ]
        : []),
      ...(form.sexSpecify
        ? [
            {
              linkId: "stateOfNewbornSexSpecify",
              text: "Specify",
              answer: [{ valueString: form.sexSpecify }],
            },
          ]
        : []),
      ...(form.newbornName
        ? [
            {
              linkId: "newbornName",
              text: "Newborn's Name",
              answer: [{ valueString: form.newbornName }],
            },
          ]
        : []),
      ...(form.stateOfNewborn?.length > 0
        ? stateCodingFormat.map((coding) => ({
            linkId: `stateOfNewborn`,
            text: `State of Newborn`,
            answer: [{ valueCoding: coding }],
          }))
        : []),
    ],
  };

  return updatedQuestionnaireResponse;
};

export const getPostpartumEncounterQuestionnaireResponse = (
  form: PostpartumAmendmentEncounterFormType,
  status: QuestionnaireResponse["status"],
  patientId: string,
  encounterId: string,
  questionnaire: Questionnaire,
  practitionerId: string,
  previousQuestionnaireResponse?: QuestionnaireResponse,
) => {
  const questionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(previousQuestionnaireResponse
      ? {
          id: previousQuestionnaireResponse.id,
        }
      : {}),
    status,
    subject: { reference: joinReferenceString("Patient", patientId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaire.id!),
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    authored: new Date().toISOString(),
    item: [
      ...(form.encounterDetails?.encounterTemplate
        ? [
            {
              linkId: "encounterTemplate",
              answer: [{ valueCoding: form.encounterDetails.encounterTemplate }],
            },
          ]
        : []),
      ...(form.encounterDetails?.encounterType
        ? [
            {
              linkId: "encounterType",
              answer: [{ valueCoding: form.encounterDetails.encounterType }],
            },
          ]
        : []),
      ...(form.encounterDetails?.encounterLocation
        ? [
            {
              linkId: "encounterLocation",
              answer: [{ valueCoding: form.encounterDetails.encounterLocation }],
            },
          ]
        : []),
      ...getEncounterServiceDateDetailQuestionnaireItems(form.encounterDetails),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoReason
        ? [
            {
              linkId: "encounterAmendmentInfoReason",
              answer: [
                typeof form.encounterAmendmentInfo.encounterAmendmentInfoReason === "string"
                  ? { valueString: form.encounterAmendmentInfo.encounterAmendmentInfoReason }
                  : { valueCoding: form.encounterAmendmentInfo.encounterAmendmentInfoReason },
              ],
            },
          ]
        : []),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoSpecify
        ? [
            {
              linkId: "encounterAmendmentInfoSpecify",
              answer: [{ valueString: form.encounterAmendmentInfo.encounterAmendmentInfoSpecify }],
            },
          ]
        : []),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoNotes
        ? [
            {
              linkId: "encounterAmendmentInfoNotes",
              answer: [{ valueString: form.encounterAmendmentInfo.encounterAmendmentInfoNotes }],
            },
          ]
        : []),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoNotifyClient
        ? [
            {
              linkId: "encounterAmendmentInfoNotifyClient",
              answer: [
                { valueBoolean: form.encounterAmendmentInfo.encounterAmendmentInfoNotifyClient },
              ],
            },
          ]
        : []),
      ...(form.notes
        ? [
            {
              linkId: "notes",
              answer: [
                {
                  valueString: form.notes,
                },
              ],
            },
          ]
        : []),
      ...(form.encounterClientSummary
        ? [
            {
              linkId: "encounterClientSummary",
              answer: [
                {
                  valueString: form.encounterClientSummary,
                },
              ],
            },
          ]
        : []),
      ...getEncounterDiagnosesItems(form.encounterClientDiagnoses),
    ],
  } as QuestionnaireResponse;

  return questionnaireResponse;
};

export const getNewbornPostpartumInputsOutputsQuestionnaireResponse = ({
  clientId,
  form,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetNewbornPostpartumInputsOutputsQuestionnaireResponseParams) => {
  // Helper function to validate decimal values
  const isValidDecimal = (value: unknown): value is number => {
    return (
      value !== undefined &&
      value !== null &&
      value !== 0 &&
      typeof value === "number" &&
      !isNaN(value)
    );
  };

  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      ...(form.inputOutputFeedingType
        ? [
            {
              linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingType,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.FeedingType,
              answer: [{ valueString: form.inputOutputFeedingType }],
            },
          ]
        : []),
      ...(isValidDecimal(form.inputOutputFeedingSupplementAmount)
        ? [
            {
              linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmount,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.BreastChestFeedingsAmount,
              answer: [{ valueDecimal: form.inputOutputFeedingSupplementAmount }],
            },
          ]
        : []),
      ...(isValidDecimal(form.inputOutputFeedingLength)
        ? [
            {
              linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingLength,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.FeedingLength,
              answer: [{ valueDecimal: form.inputOutputFeedingLength }],
            },
          ]
        : []),
      ...(form.inputOutputFeedingBehavior?.length
        ? [
            {
              linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingBehavior,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.FeedingBehavior,
              answer: form.inputOutputFeedingBehavior.map((behavior) => ({
                valueString: behavior,
              })),
            },
          ]
        : []),
      ...(form.inputOutputVoiding?.length
        ? [
            {
              linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.Voiding,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.Voiding,
              answer: form.inputOutputVoiding.map((voiding) => ({
                valueString: voiding,
              })),
            },
          ]
        : []),
      ...(form.inputOutputVoidingNormal && form.inputOutputVoidingNormal.trim() !== ""
        ? [
            {
              linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.VoidingNormal,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.Normal,
              answer: [{ valueString: form.inputOutputVoidingNormal }],
            },
          ]
        : []),
      ...(form.inputOutputVoidingNotNormal && form.inputOutputVoidingNotNormal.trim() !== ""
        ? [
            {
              linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.VoidingNotNormal,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.NotNormal,
              answer: [{ valueString: form.inputOutputVoidingNotNormal }],
            },
          ]
        : []),
      ...(form.inputOutputBowel?.length
        ? [
            {
              linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.Bowel,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.Bowel,
              answer: form.inputOutputBowel.map((bowel) => ({
                valueString: bowel,
              })),
            },
          ]
        : []),
      ...(form.inputOutputBowelNormal && form.inputOutputBowelNormal.trim() !== ""
        ? [
            {
              linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.BowelNormal,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.Normal,
              answer: [{ valueString: form.inputOutputBowelNormal }],
            },
          ]
        : []),
      ...(form.inputOutputBowelNotNormal && form.inputOutputBowelNotNormal.trim() !== ""
        ? [
            {
              linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.BowelNotNormal,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.NotNormal,
              answer: [{ valueString: form.inputOutputBowelNotNormal }],
            },
          ]
        : []),
      ...(form.inputOutputNotes
        ? [
            {
              linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.Notes,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.Notes,
              answer: [{ valueString: form.inputOutputNotes }],
            },
          ]
        : []),
      // Supplement fields
      ...(form.inputOutputFeedingSupplement?.length
        ? [
            {
              linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplement,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementType,
              answer: form.inputOutputFeedingSupplement.map((supplement) => ({
                valueString: supplement,
              })),
            },
          ]
        : []),
      ...(isValidDecimal(form.inputOutputFeedingSupplementAmountPumpedMilk)
        ? [
            {
              linkId:
                NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountPumpedMilk,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAmount,
              answer: [{ valueDecimal: form.inputOutputFeedingSupplementAmountPumpedMilk }],
            },
          ]
        : []),
      ...(form.inputOutputFeedingSupplementAmountUnitPumpedMilk
        ? [
            {
              linkId:
                NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountUnitPumpedMilk,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAverageAmount,
              answer: [{ valueCoding: form.inputOutputFeedingSupplementAmountUnitPumpedMilk }],
            },
          ]
        : []),
      ...(isValidDecimal(form.inputOutputFeedingSupplementAverageAmountPumpedMilk)
        ? [
            {
              linkId:
                NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAverageAmountPumpedMilk,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAverageAmount,
              answer: [{ valueDecimal: form.inputOutputFeedingSupplementAverageAmountPumpedMilk }],
            },
          ]
        : []),
      ...(isValidDecimal(form.inputOutputFeedingSupplementAmountDonorMilk)
        ? [
            {
              linkId:
                NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountDonorMilk,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAmount,
              answer: [{ valueDecimal: form.inputOutputFeedingSupplementAmountDonorMilk }],
            },
          ]
        : []),
      ...(form.inputOutputFeedingSupplementAmountUnitDonorMilk
        ? [
            {
              linkId:
                NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountUnitDonorMilk,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAverageAmount,
              answer: [{ valueCoding: form.inputOutputFeedingSupplementAmountUnitDonorMilk }],
            },
          ]
        : []),
      ...(isValidDecimal(form.inputOutputFeedingSupplementAverageAmountDonorMilk)
        ? [
            {
              linkId:
                NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAverageAmountDonorMilk,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAverageAmount,
              answer: [{ valueDecimal: form.inputOutputFeedingSupplementAverageAmountDonorMilk }],
            },
          ]
        : []),
      ...(isValidDecimal(form.inputOutputFeedingSupplementAmountFormula)
        ? [
            {
              linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountFormula,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAmount,
              answer: [{ valueDecimal: form.inputOutputFeedingSupplementAmountFormula }],
            },
          ]
        : []),
      ...(form.inputOutputFeedingSupplementAmountUnitFormula
        ? [
            {
              linkId:
                NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAmountUnitFormula,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAverageAmount,
              answer: [{ valueCoding: form.inputOutputFeedingSupplementAmountUnitFormula }],
            },
          ]
        : []),
      ...(isValidDecimal(form.inputOutputFeedingSupplementAverageAmountFormula)
        ? [
            {
              linkId:
                NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementAverageAmountFormula,
              text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAverageAmount,
              answer: [{ valueDecimal: form.inputOutputFeedingSupplementAverageAmountFormula }],
            },
          ]
        : []),
      // Formset fields
      ...(form.inputOutputFeedingSupplementFormset
        ? Object.entries(form.inputOutputFeedingSupplementFormset).map(([key, formsetItem]) => ({
            linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementFormset,
            text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementFormset,
            item: [
              {
                linkId: NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementFormsetCode,
                answer: [{ valueString: formsetItem.inputOutputFeedingSupplementFormsetCode }],
              },
              ...(formsetItem.inputOutputFeedingSupplementFormsetType?.length
                ? [
                    {
                      linkId:
                        NewbornPostpartumInputsOutputsQuestionsLinkIds.FeedingSupplementFormsetType,
                      text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementType,
                      answer: formsetItem.inputOutputFeedingSupplementFormsetType.map((type) => ({
                        valueString: type,
                      })),
                    },
                  ]
                : []),
              ...(isValidDecimal(
                formsetItem.inputOutputFormsetTypeFeedingSupplementAmountPumpedMilk,
              )
                ? [
                    {
                      linkId:
                        NewbornPostpartumInputsOutputsQuestionsLinkIds.FormsetTypeFeedingSupplementAmountPumpedMilk,
                      text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAmount,
                      answer: [
                        {
                          valueDecimal:
                            formsetItem.inputOutputFormsetTypeFeedingSupplementAmountPumpedMilk,
                        },
                      ],
                    },
                  ]
                : []),
              ...(formsetItem.inputOutputFormsetTypeFeedingAmountUnitPumpedMilk
                ? [
                    {
                      linkId:
                        NewbornPostpartumInputsOutputsQuestionsLinkIds.FormsetTypeFeedingAmountUnitPumpedMilk,
                      text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAverageAmount,
                      answer: [
                        {
                          valueCoding:
                            formsetItem.inputOutputFormsetTypeFeedingAmountUnitPumpedMilk,
                        },
                      ],
                    },
                  ]
                : []),
              ...(isValidDecimal(formsetItem.inputOutputFormsetTypeFeedingAverageAmountPumpedMilk)
                ? [
                    {
                      linkId:
                        NewbornPostpartumInputsOutputsQuestionsLinkIds.FormsetTypeFeedingAverageAmountPumpedMilk,
                      text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAverageAmount,
                      answer: [
                        {
                          valueDecimal:
                            formsetItem.inputOutputFormsetTypeFeedingAverageAmountPumpedMilk,
                        },
                      ],
                    },
                  ]
                : []),
              ...(isValidDecimal(formsetItem.inputOutputFormsetTypeFeedingSupplementAmountDonorMilk)
                ? [
                    {
                      linkId:
                        NewbornPostpartumInputsOutputsQuestionsLinkIds.FormsetTypeFeedingSupplementAmountDonorMilk,
                      text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAmount,
                      answer: [
                        {
                          valueDecimal:
                            formsetItem.inputOutputFormsetTypeFeedingSupplementAmountDonorMilk,
                        },
                      ],
                    },
                  ]
                : []),
              ...(formsetItem.inputOutputFormsetTypeFeedingAmountUnitDonorMilk
                ? [
                    {
                      linkId:
                        NewbornPostpartumInputsOutputsQuestionsLinkIds.FormsetTypeFeedingAmountUnitDonorMilk,
                      text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAverageAmount,
                      answer: [
                        {
                          valueCoding: formsetItem.inputOutputFormsetTypeFeedingAmountUnitDonorMilk,
                        },
                      ],
                    },
                  ]
                : []),
              ...(isValidDecimal(formsetItem.inputOutputFormsetTypeFeedingAverageAmountDonorMilk)
                ? [
                    {
                      linkId:
                        NewbornPostpartumInputsOutputsQuestionsLinkIds.FormsetTypeFeedingAverageAmountDonorMilk,
                      text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAverageAmount,
                      answer: [
                        {
                          valueDecimal:
                            formsetItem.inputOutputFormsetTypeFeedingAverageAmountDonorMilk,
                        },
                      ],
                    },
                  ]
                : []),
              ...(isValidDecimal(formsetItem.inputOutputFormsetTypeFeedingSupplementAmountFormula)
                ? [
                    {
                      linkId:
                        NewbornPostpartumInputsOutputsQuestionsLinkIds.FormsetTypeFeedingSupplementAmountFormula,
                      text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAmount,
                      answer: [
                        {
                          valueDecimal:
                            formsetItem.inputOutputFormsetTypeFeedingSupplementAmountFormula,
                        },
                      ],
                    },
                  ]
                : []),
              ...(formsetItem.inputOutputFormsetTypeFeedingAmountUnitFormula
                ? [
                    {
                      linkId:
                        NewbornPostpartumInputsOutputsQuestionsLinkIds.FormsetTypeFeedingAmountUnitFormula,
                      text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAverageAmount,
                      answer: [
                        { valueCoding: formsetItem.inputOutputFormsetTypeFeedingAmountUnitFormula },
                      ],
                    },
                  ]
                : []),
              ...(isValidDecimal(formsetItem.inputOutputFormsetTypeFeedingAverageAmountFormula)
                ? [
                    {
                      linkId:
                        NewbornPostpartumInputsOutputsQuestionsLinkIds.FormsetTypeFeedingAverageAmountFormula,
                      text: NewbornPostpartumInputsOutputsQuestionsDisplayNames.SupplementAverageAmount,
                      answer: [
                        {
                          valueDecimal:
                            formsetItem.inputOutputFormsetTypeFeedingAverageAmountFormula,
                        },
                      ],
                    },
                  ]
                : []),
            ],
          }))
        : []),
    ],
  };

  return updatedQuestionnaireResponse;
};

export const getPostpartumFeedingQuestionnaireResponse = ({
  form,
  clientId,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetPostpartumFeedingQuestionnaireResponseParams): QuestionnaireResponse => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      {
        linkId: PostpartumFeedingQuestionsLinkIds.PostpartumFeeding,
        text: PostpartumFeedingQuestionsDisplayNames.Feeding,
        item: [
          ...(form.postpartumFeedingType?.length
            ? [
                {
                  linkId: PostpartumFeedingQuestionsLinkIds.PostpartumFeedingType,
                  text: PostpartumFeedingQuestionsDisplayNames.PostpartumFeedingType,
                  answer: form.postpartumFeedingType.map((value) => ({ valueString: value })),
                },
              ]
            : []),
          ...(form.postpartumFeedingSupplement?.length
            ? [
                {
                  linkId: PostpartumFeedingQuestionsLinkIds.PostpartumFeedingSupplement,
                  text: PostpartumFeedingQuestionsDisplayNames.PostpartumFeedingSupplement,
                  answer: form.postpartumFeedingSupplement.map((value) => ({ valueString: value })),
                },
              ]
            : []),
          ...(form.postpartumFeedingWith && form.postpartumFeedingWith.length > 0
            ? [
                {
                  linkId: PostpartumFeedingQuestionsLinkIds.PostpartumFeedingWith,
                  text: PostpartumFeedingQuestionsDisplayNames.PostpartumFeedingWith,
                  answer: form.postpartumFeedingWith.map((value) => ({ valueString: value })),
                },
              ]
            : []),
          ...(form.postpartumFeedingSupply
            ? [
                {
                  linkId: PostpartumFeedingQuestionsLinkIds.PostpartumFeedingSupply,
                  text: PostpartumFeedingQuestionsDisplayNames.PostpartumFeedingSupply,
                  answer: [{ valueString: form.postpartumFeedingSupply }],
                },
              ]
            : []),
          ...(form.postpartumFeedingPain
            ? [
                {
                  linkId: PostpartumFeedingQuestionsLinkIds.PostpartumFeedingPain,
                  text: PostpartumFeedingQuestionsDisplayNames.PostpartumFeedingPain,
                  answer: [{ valueString: form.postpartumFeedingPain }],
                },
              ]
            : []),
          ...(form.postpartumFeedingRedness
            ? [
                {
                  linkId: PostpartumFeedingQuestionsLinkIds.PostpartumFeedingRedness,
                  text: PostpartumFeedingQuestionsDisplayNames.PostpartumFeedingRedness,
                  answer: [{ valueString: form.postpartumFeedingRedness }],
                },
              ]
            : []),
          ...(form.postpartumFeedingDucts
            ? [
                {
                  linkId: PostpartumFeedingQuestionsLinkIds.PostpartumFeedingDucts,
                  text: PostpartumFeedingQuestionsDisplayNames.PostpartumFeedingDucts,
                  answer: [{ valueString: form.postpartumFeedingDucts }],
                },
              ]
            : []),
          ...(form.postpartumFeedingNotes
            ? [
                {
                  linkId: PostpartumFeedingQuestionsLinkIds.PostpartumFeedingNotes,
                  text: PostpartumFeedingQuestionsDisplayNames.PostpartumFeedingNotes,
                  answer: [{ valueString: form.postpartumFeedingNotes }],
                },
              ]
            : []),
        ],
      },
    ],
  };

  if (questionnaireResponse?.id) {
    updatedQuestionnaireResponse.id = questionnaireResponse.id;
  }

  return updatedQuestionnaireResponse;
};

export const getPostpartumLochiaQuestionnaireResponse = ({
  form,
  clientId,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetPostpartumLochiaQuestionnaireResponseParams): QuestionnaireResponse => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      {
        linkId: PostpartumLochiaQuestionsLinkIds.PostpartumLochia,
        text: PostpartumLochiaQuestionsDisplayNames.PostpartumLochia,
        item: [
          ...(form.postpartumLochiaType?.length
            ? [
                {
                  linkId: PostpartumLochiaQuestionsLinkIds.PostpartumLochiaType,
                  text: PostpartumLochiaQuestionsDisplayNames.PostpartumLochiaType,
                  answer: form.postpartumLochiaType.map((value) => ({ valueString: value })),
                },
              ]
            : []),
          ...(form.postpartumLochiaNotes
            ? [
                {
                  linkId: PostpartumLochiaQuestionsLinkIds.PostpartumLochiaNotes,
                  text: PostpartumLochiaQuestionsDisplayNames.PostpartumLochiaNotes,
                  answer: [{ valueString: form.postpartumLochiaNotes }],
                },
              ]
            : []),
        ],
      },
    ],
  };

  if (questionnaireResponse?.id) {
    updatedQuestionnaireResponse.id = questionnaireResponse.id;
  }

  return updatedQuestionnaireResponse;
};

export const getPostpartumActivityNutritionQuestionnaireResponse = ({
  form,
  clientId,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetPostpartumActivityNutritionQuestionnaireResponseParams): QuestionnaireResponse => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      {
        linkId: "postpartumActivityNutrition",
        text: "Postpartum Activity & Nutrition",
        item: [
          ...(form.postpartumActivityNutritionType?.length
            ? [
                {
                  linkId: "postpartumActivityNutritionType",
                  text: "What activities do you perform regularly?",
                  answer: form.postpartumActivityNutritionType.map((value) => ({
                    valueString: value,
                  })),
                },
              ]
            : []),
          ...(form.postpartumActivityNutritionTypeOther
            ? [
                {
                  linkId: "postpartumActivityNutritionTypeOther",
                  text: "Please specify",
                  answer: [{ valueString: form.postpartumActivityNutritionTypeOther }],
                },
              ]
            : []),
          ...(form.postpartumActivityNutritionDietaryRestrictions?.length
            ? [
                {
                  linkId: "postpartumActivityNutritionDietaryRestrictions",
                  text: "Do you have any dietary restrictions?",
                  answer: form.postpartumActivityNutritionDietaryRestrictions.map((value) => ({
                    valueString: value,
                  })),
                },
              ]
            : []),
          ...(form.postpartumActivityNutritionBeveragesCaffeine
            ? [
                {
                  linkId: "postpartumActivityNutritionBeveragesCaffeine",
                  text: "During the past month, how often did you drink beverages that contain caffeine?",
                  answer: [{ valueString: form.postpartumActivityNutritionBeveragesCaffeine }],
                },
              ]
            : []),
          ...(form.postpartumActivityNutritionMeals
            ? [
                {
                  linkId: "postpartumActivityNutritionMeals",
                  text: "How often do you consume meals that are well balanced and with unprocessed foods?",
                  answer: [{ valueString: form.postpartumActivityNutritionMeals }],
                },
              ]
            : []),
          ...(form.postpartumActivityNutritionWater
            ? [
                {
                  linkId: "postpartumActivityNutritionWater",
                  text: "How many 8 oz cups of water do you drink in a day?",
                  answer: [{ valueString: form.postpartumActivityNutritionWater }],
                },
              ]
            : []),
          ...(form.postpartumActivityNutritionBeverages?.length
            ? [
                {
                  linkId: "postpartumActivityNutritionBeverages",
                  text: "Besides water, what else do you drink?",
                  answer: form.postpartumActivityNutritionBeverages.map((value) => ({
                    valueString: value,
                  })),
                },
              ]
            : []),
          ...(form.postpartumActivityNutritionNotes
            ? [
                {
                  linkId: "postpartumActivityNutritionNotes",
                  text: "Notes",
                  answer: [{ valueString: form.postpartumActivityNutritionNotes }],
                },
              ]
            : []),
        ],
      },
    ],
  };

  if (questionnaireResponse?.id) {
    updatedQuestionnaireResponse.id = questionnaireResponse.id;
  }

  return updatedQuestionnaireResponse;
};

export const getPostpartumMedicationAndTherapiesQuestionnaireResponse = ({
  form,
  clientId,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetPostpartumMedicationAndTherapiesQuestionnaireResponseParams): QuestionnaireResponse => {
  const medications = Object.values(form.postpartumMedicationAndTherapiesMedicationsFormset || []);
  const therapies = Object.values(form.postpartumMedicationAndTherapiesTherapiesFormset || []);

  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      {
        linkId: "postpartumMedicationAndTherapies",
        text: "Postpartum Medication & Therapies",
        item: [
          ...medications.map((medication) => ({
            linkId: "postpartumMedicationAndTherapiesMedicationsFormset",
            text: "Medication",
            item: [
              ...(medication.postpartumMedicationAndTherapiesMedicationsFormsetCode
                ? [
                    {
                      linkId: "postpartumMedicationAndTherapiesMedicationsFormsetCode",
                      answer: [
                        {
                          valueString:
                            medication.postpartumMedicationAndTherapiesMedicationsFormsetCode,
                        },
                      ],
                    },
                  ]
                : []),
              ...(medication.postpartumMedicationAndTherapiesMedicationsFormsetMedication
                ? [
                    {
                      linkId: "postpartumMedicationAndTherapiesMedicationsFormsetMedication",
                      text: "Medication",
                      answer: [
                        {
                          valueCoding:
                            medication.postpartumMedicationAndTherapiesMedicationsFormsetMedication,
                        },
                      ],
                    },
                  ]
                : []),
              ...(medication.postpartumMedicationAndTherapiesMedicationsFormsetForm
                ? [
                    {
                      linkId: "postpartumMedicationAndTherapiesMedicationsFormsetForm",
                      text: "Form",
                      answer: [
                        {
                          valueCoding:
                            medication.postpartumMedicationAndTherapiesMedicationsFormsetForm,
                        },
                      ],
                    },
                  ]
                : []),
              ...(medication.postpartumMedicationAndTherapiesMedicationFormDosage
                ? [
                    {
                      linkId: "postpartumMedicationAndTherapiesMedicationFormDosage",
                      item: [
                        ...(medication.postpartumMedicationAndTherapiesMedicationFormDosage
                          .postpartumMedicationAndTherapiesMedicationsFormsetDosage
                          ? [
                              {
                                linkId: "postpartumMedicationAndTherapiesMedicationsFormsetDosage",
                                text: "Dosage",
                                answer: [
                                  {
                                    valueCoding:
                                      medication
                                        .postpartumMedicationAndTherapiesMedicationFormDosage
                                        .postpartumMedicationAndTherapiesMedicationsFormsetDosage,
                                  },
                                ],
                              },
                            ]
                          : []),
                        ...(medication.postpartumMedicationAndTherapiesMedicationFormDosage
                          .postpartumMedicationAndTherapiesMedicationsFormsetRoute
                          ? [
                              {
                                linkId: "postpartumMedicationAndTherapiesMedicationsFormsetRoute",
                                text: "Route",
                                answer: [
                                  {
                                    valueCoding:
                                      medication
                                        .postpartumMedicationAndTherapiesMedicationFormDosage
                                        .postpartumMedicationAndTherapiesMedicationsFormsetRoute,
                                  },
                                ],
                              },
                            ]
                          : []),
                      ],
                    },
                  ]
                : []),
              ...(medication.postpartumMedicationAndTherapiesMedicationsFrequencyDuration
                ?.postpartumMedicationAndTherapiesMedicationsFormsetFrequency ||
              medication.postpartumMedicationAndTherapiesMedicationsFrequencyDuration
                ?.postpartumMedicationAndTherapiesMedicationsFormsetDuration ||
              medication.postpartumMedicationAndTherapiesMedicationsFrequencyDuration
                ?.postpartumMedicationAndTherapiesMedicationsFrequencyDurationXDays
                ? [
                    {
                      linkId: "postpartumMedicationAndTherapiesMedicationsFrequencyDuration",
                      item: [
                        ...(medication.postpartumMedicationAndTherapiesMedicationsFrequencyDuration
                          ?.postpartumMedicationAndTherapiesMedicationsFormsetFrequency
                          ? [
                              {
                                linkId:
                                  "postpartumMedicationAndTherapiesMedicationsFormsetFrequency",
                                text: "Frequency",
                                answer: [
                                  {
                                    valueCoding:
                                      medication
                                        .postpartumMedicationAndTherapiesMedicationsFrequencyDuration
                                        .postpartumMedicationAndTherapiesMedicationsFormsetFrequency,
                                  },
                                ],
                              },
                            ]
                          : []),
                        ...(medication.postpartumMedicationAndTherapiesMedicationsFrequencyDuration
                          ?.postpartumMedicationAndTherapiesMedicationsFormsetDuration
                          ? [
                              {
                                linkId:
                                  "postpartumMedicationAndTherapiesMedicationsFormsetDuration",
                                text: "Duration",
                                answer: [
                                  {
                                    valueCoding:
                                      medication
                                        .postpartumMedicationAndTherapiesMedicationsFrequencyDuration
                                        .postpartumMedicationAndTherapiesMedicationsFormsetDuration,
                                  },
                                ],
                              },
                            ]
                          : []),
                        ...(medication.postpartumMedicationAndTherapiesMedicationsFrequencyDuration
                          ?.postpartumMedicationAndTherapiesMedicationsFrequencyDurationXDays
                          ? [
                              {
                                linkId:
                                  "postpartumMedicationAndTherapiesMedicationsFrequencyDurationXDays",
                                text: "# of Days",
                                answer: [
                                  {
                                    valueDecimal:
                                      medication
                                        .postpartumMedicationAndTherapiesMedicationsFrequencyDuration
                                        .postpartumMedicationAndTherapiesMedicationsFrequencyDurationXDays,
                                  },
                                ],
                              },
                            ]
                          : []),
                      ],
                    },
                  ]
                : []),
              ...(medication.postpartumMedicationAndTherapiesMedicationsFormsetSpecialInstructions
                ? [
                    {
                      linkId:
                        "postpartumMedicationAndTherapiesMedicationsFormsetSpecialInstructions",
                      text: "Special Instructions",
                      answer: [
                        {
                          valueCoding:
                            medication.postpartumMedicationAndTherapiesMedicationsFormsetSpecialInstructions,
                        },
                      ],
                    },
                  ]
                : []),
              ...(medication.postpartumMedicationAndTherapiesMedicationsFormsetPRNReason
                ? [
                    {
                      linkId: "postpartumMedicationAndTherapiesMedicationsFormsetPRNReason",
                      text: "PRN Reason",
                      answer: [
                        {
                          valueCoding:
                            medication.postpartumMedicationAndTherapiesMedicationsFormsetPRNReason,
                        },
                      ],
                    },
                  ]
                : []),
            ],
          })),
          ...therapies.map((therapy) => ({
            linkId: "postpartumMedicationAndTherapiesTherapiesFormset",
            text: "Therapy",
            item: [
              ...(therapy.postpartumMedicationAndTherapiesTherapiesFormsetCode
                ? [
                    {
                      linkId: "postpartumMedicationAndTherapiesTherapiesFormsetCode",
                      answer: [
                        {
                          valueString: therapy.postpartumMedicationAndTherapiesTherapiesFormsetCode,
                        },
                      ],
                    },
                  ]
                : []),
              ...(therapy.postpartumMedicationAndTherapiesTherapiesFormsetTherapy
                ? [
                    {
                      linkId: "postpartumMedicationAndTherapiesTherapiesFormsetTherapy",
                      text: "Therapy",
                      answer: [
                        {
                          valueString:
                            therapy.postpartumMedicationAndTherapiesTherapiesFormsetTherapy,
                        },
                      ],
                    },
                  ]
                : []),
              ...(therapy.postpartumMedicationAndTherapiesTherapiesReason
                ? [
                    {
                      linkId: "postpartumMedicationAndTherapiesTherapiesReason",
                      text: "Reason",
                      answer: [
                        { valueCoding: therapy.postpartumMedicationAndTherapiesTherapiesReason },
                      ],
                    },
                  ]
                : []),
              ...(therapy.postpartumMedicationAndTherapiesTherapiesReasonOther
                ? [
                    {
                      linkId: "postpartumMedicationAndTherapiesTherapiesReasonOther",
                      text: "Other, please specify",
                      answer: [
                        {
                          valueString: therapy.postpartumMedicationAndTherapiesTherapiesReasonOther,
                        },
                      ],
                    },
                  ]
                : []),
            ],
          })),
          ...(form.postpartumMedicationAndTherapiesNotes
            ? [
                {
                  linkId: "postpartumMedicationAndTherapiesNotes",
                  text: "Notes",
                  answer: [{ valueString: form.postpartumMedicationAndTherapiesNotes }],
                },
              ]
            : []),
        ],
      },
    ],
  };

  if (questionnaireResponse?.id) {
    updatedQuestionnaireResponse.id = questionnaireResponse.id;
  }

  return updatedQuestionnaireResponse;
};

export const getGynecologicalEncounterQuestionnaireResponse = (
  form: GynecologicalAmendmentEncounterFormType,
  status: QuestionnaireResponse["status"],
  patientId: string,
  encounterId: string,
  questionnaire: Questionnaire,
  practitionerId: string,
  previousQuestionnaireResponse?: QuestionnaireResponse,
) => {
  const questionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(previousQuestionnaireResponse
      ? {
          id: previousQuestionnaireResponse.id,
        }
      : {}),
    status,
    subject: { reference: joinReferenceString("Patient", patientId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaire.id!),
    authored: new Date().toISOString(),
    item: [
      ...(form.encounterDetails?.encounterTemplate
        ? [
            {
              linkId: "encounterTemplate",
              answer: [{ valueCoding: form.encounterDetails.encounterTemplate }],
            },
          ]
        : []),
      ...(form.encounterDetails?.encounterType
        ? [
            {
              linkId: "encounterType",
              answer: [{ valueCoding: form.encounterDetails.encounterType }],
            },
          ]
        : []),
      ...(form.encounterDetails?.encounterLocation
        ? [
            {
              linkId: "encounterLocation",
              answer: [{ valueCoding: form.encounterDetails.encounterLocation }],
            },
          ]
        : []),
      ...getEncounterServiceDateDetailQuestionnaireItems(form.encounterDetails),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoReason
        ? [
            {
              linkId: "encounterAmendmentInfoReason",
              answer: [
                typeof form.encounterAmendmentInfo.encounterAmendmentInfoReason === "string"
                  ? { valueString: form.encounterAmendmentInfo.encounterAmendmentInfoReason }
                  : { valueCoding: form.encounterAmendmentInfo.encounterAmendmentInfoReason },
              ],
            },
          ]
        : []),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoSpecify
        ? [
            {
              linkId: "encounterAmendmentInfoSpecify",
              answer: [{ valueString: form.encounterAmendmentInfo.encounterAmendmentInfoSpecify }],
            },
          ]
        : []),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoNotes
        ? [
            {
              linkId: "encounterAmendmentInfoNotes",
              answer: [{ valueString: form.encounterAmendmentInfo.encounterAmendmentInfoNotes }],
            },
          ]
        : []),
      ...(form.encounterAmendmentInfo?.encounterAmendmentInfoNotifyClient
        ? [
            {
              linkId: "encounterAmendmentInfoNotifyClient",
              answer: [
                { valueBoolean: form.encounterAmendmentInfo.encounterAmendmentInfoNotifyClient },
              ],
            },
          ]
        : []),
      ...(form.notes
        ? [
            {
              linkId: "notes",
              answer: [
                {
                  valueString: form.notes,
                },
              ],
            },
          ]
        : []),
      ...(form.encounterClientSummary
        ? [
            {
              linkId: "encounterClientSummary",
              answer: [
                {
                  valueString: form.encounterClientSummary,
                },
              ],
            },
          ]
        : []),
      ...getEncounterDiagnosesItems(form.encounterClientDiagnoses),
    ],
  } as QuestionnaireResponse;

  return questionnaireResponse;
};

export const getPostpartumSleepQuestionnaireResponse = ({
  form,
  clientId,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetPostpartumSleepQuestionnaireResponseParams): QuestionnaireResponse => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      {
        linkId: PostpartumSleepQuestionsLinkIds.PostpartumSleep,
        text: PostpartumSleepQuestionsDisplayNames.PostpartumSleep,
        item: [
          ...(form.postpartumSleepHours
            ? [
                {
                  linkId: PostpartumSleepQuestionsLinkIds.PostpartumSleepHours,
                  text: PostpartumSleepQuestionsDisplayNames.PostpartumSleepHours,
                  answer: [{ valueDecimal: form.postpartumSleepHours }],
                },
              ]
            : []),
          ...(form.postpartumSleepWakeUp
            ? [
                {
                  linkId: PostpartumSleepQuestionsLinkIds.PostpartumSleepWakeUp,
                  text: PostpartumSleepQuestionsDisplayNames.PostpartumSleepWakeUp,
                  answer: [{ valueString: form.postpartumSleepWakeUp }],
                },
              ]
            : []),
          ...(form.postpartumSleepFallingAsleep
            ? [
                {
                  linkId: PostpartumSleepQuestionsLinkIds.PostpartumSleepFallingAsleep,
                  text: PostpartumSleepQuestionsDisplayNames.PostpartumSleepFallingAsleep,
                  answer: [{ valueString: form.postpartumSleepFallingAsleep }],
                },
              ]
            : []),
          ...(form.postpartumSleepStayingAsleep
            ? [
                {
                  linkId: PostpartumSleepQuestionsLinkIds.PostpartumSleepStayingAsleep,
                  text: PostpartumSleepQuestionsDisplayNames.PostpartumSleepStayingAsleep,
                  answer: [{ valueString: form.postpartumSleepStayingAsleep }],
                },
              ]
            : []),
          ...(form.postpartumSleepBreathing
            ? [
                {
                  linkId: PostpartumSleepQuestionsLinkIds.PostpartumSleepBreathing,
                  text: PostpartumSleepQuestionsDisplayNames.PostpartumSleepBreathing,
                  answer: [{ valueString: form.postpartumSleepBreathing }],
                },
              ]
            : []),
          ...(form.postpartumSleepNotes
            ? [
                {
                  linkId: PostpartumSleepQuestionsLinkIds.PostpartumSleepNotes,
                  text: PostpartumSleepQuestionsDisplayNames.PostpartumSleepNotes,
                  answer: [{ valueString: form.postpartumSleepNotes }],
                },
              ]
            : []),
        ],
      },
    ],
  };

  if (questionnaireResponse?.id) {
    updatedQuestionnaireResponse.id = questionnaireResponse.id;
  }

  return updatedQuestionnaireResponse;
};

export const getPostpartumSocialHealthQuestionnaireResponse = ({
  form,
  clientId,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetPostpartumSocialHealthQuestionnaireResponseParams): QuestionnaireResponse => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      {
        linkId: PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealth,
        text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealth,
        item: [
          ...(form.postpartumSocialHealthSmoke
            ? [
                {
                  linkId: PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSmoke,
                  text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthSmoke,
                  answer: [
                    {
                      valueCoding: {
                        code: form.postpartumSocialHealthSmoke,
                        display: form.postpartumSocialHealthSmoke,
                      },
                    },
                  ],
                },
              ]
            : []),
          ...(form.postpartumSocialHealthSmokeAmount
            ? [
                {
                  linkId: PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSmokeAmount,
                  text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthAmount,
                  answer: [{ valueString: form.postpartumSocialHealthSmokeAmount }],
                },
              ]
            : []),
          ...(form.postpartumSocialHealthSmokeFrequency
            ? [
                {
                  linkId:
                    PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSmokeFrequency,
                  text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthSmokeFrequency,
                  answer: [{ valueString: form.postpartumSocialHealthSmokeFrequency }],
                },
              ]
            : []),
          ...(form.postpartumSocialHealthDrink
            ? [
                {
                  linkId: PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrink,
                  text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthDrink,
                  answer: [
                    {
                      valueCoding: {
                        code: form.postpartumSocialHealthDrink,
                        display: form.postpartumSocialHealthDrink,
                      },
                    },
                  ],
                },
              ]
            : []),
          ...(form.postpartumSocialHealthDrinkAmount
            ? [
                {
                  linkId: PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrinkAmount,
                  text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthAmount,
                  answer: [{ valueString: form.postpartumSocialHealthDrinkAmount }],
                },
              ]
            : []),
          ...(form.postpartumSocialHealthDrinkFrequency
            ? [
                {
                  linkId:
                    PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrinkFrequency,
                  text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthDrinkFrequency,
                  answer: [{ valueString: form.postpartumSocialHealthDrinkFrequency }],
                },
              ]
            : []),
          ...(form.postpartumSocialHealthDrugs
            ? [
                {
                  linkId: PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrugs,
                  text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthDrugs,
                  answer: [
                    {
                      valueCoding: {
                        code: form.postpartumSocialHealthDrugs,
                        display: form.postpartumSocialHealthDrugs,
                      },
                    },
                  ],
                },
              ]
            : []),
          ...(form.postpartumSocialHealthDrugsType
            ? [
                {
                  linkId: PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthDrugsType,
                  text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthDrugsType,
                  answer: [{ valueString: form.postpartumSocialHealthDrugsType }],
                },
              ]
            : []),
          ...(form.postpartumSocialHealthWork
            ? [
                {
                  linkId: PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthWork,
                  text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthWork,
                  answer: [
                    {
                      valueCoding: {
                        code: form.postpartumSocialHealthWork,
                        display: form.postpartumSocialHealthWork,
                      },
                    },
                  ],
                },
              ]
            : []),
          ...(form.postpartumSocialHealthWorkCapacity
            ? [
                {
                  linkId: PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthWorkCapacity,
                  text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthWorkCapacity,
                  answer: [{ valueString: form.postpartumSocialHealthWorkCapacity }],
                },
              ]
            : []),
          ...(form.postpartumSocialHealthWorkFrequency
            ? [
                {
                  linkId:
                    PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthWorkFrequency,
                  text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthWorkFrequency,
                  answer: [{ valueString: form.postpartumSocialHealthWorkFrequency }],
                },
              ]
            : []),
          ...(form.postpartumSocialHealthSupport
            ? [
                {
                  linkId: PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSupport,
                  text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthSupport,
                  answer: [{ valueString: form.postpartumSocialHealthSupport }],
                },
              ]
            : []),
          ...(form.postpartumSocialHealthSupportBy?.length
            ? [
                {
                  linkId: PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthSupportBy,
                  text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthSupportBy,
                  answer: form.postpartumSocialHealthSupportBy.map((value) => ({
                    valueString: value,
                  })),
                },
              ]
            : []),
          ...(form.postpartumSocialHealthNotes
            ? [
                {
                  linkId: PostpartumSocialHealthQuestionsLinkIds.PostpartumSocialHealthNotes,
                  text: PostpartumSocialHealthQuestionsDisplayNames.PostpartumSocialHealthNotes,
                  answer: [{ valueString: form.postpartumSocialHealthNotes }],
                },
              ]
            : []),
        ],
      },
    ],
  };

  if (questionnaireResponse?.id) {
    updatedQuestionnaireResponse.id = questionnaireResponse.id;
  }

  return updatedQuestionnaireResponse;
};

export const getPostpartumEmotionalHealthQuestionnaireResponse = ({
  form,
  clientId,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetPostpartumEmotionalHealthQuestionnaireResponseParams): QuestionnaireResponse => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      {
        linkId: PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealth,
        text: PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealth,
        item: [
          ...(form.postpartumEmotionalHealthMood?.length
            ? [
                {
                  linkId: PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthMood,
                  text: PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthMood,
                  answer: form.postpartumEmotionalHealthMood.map((value) => ({
                    valueString: value,
                  })),
                },
              ]
            : []),
          ...(form.postpartumEmotionalHealthTasks
            ? [
                {
                  linkId: PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthTasks,
                  text: PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthTasks,
                  answer: [
                    {
                      valueString: form.postpartumEmotionalHealthTasks,
                    },
                  ],
                },
              ]
            : []),
          ...(form.postpartumEmotionalHealthBonding
            ? [
                {
                  linkId:
                    PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthBonding,
                  text: PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthBonding,
                  answer: [
                    {
                      valueString: form.postpartumEmotionalHealthBonding,
                    },
                  ],
                },
              ]
            : []),
          ...(form.postpartumEmotionalHealthSuicide
            ? [
                {
                  linkId:
                    PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthSuicide,
                  text: PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthSuicide,
                  answer: [
                    {
                      valueCoding: {
                        code: form.postpartumEmotionalHealthSuicide,
                        display: form.postpartumEmotionalHealthSuicide,
                      },
                    },
                  ],
                },
              ]
            : []),
          ...(form.postpartumEmotionalHealthHallucinations
            ? [
                {
                  linkId:
                    PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthHallucinations,
                  text: PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthHallucinations,
                  answer: [
                    {
                      valueCoding: {
                        code: form.postpartumEmotionalHealthHallucinations,
                        display: form.postpartumEmotionalHealthHallucinations,
                      },
                    },
                  ],
                },
              ]
            : []),
          ...(form.postpartumEmotionalHealthNotes
            ? [
                {
                  linkId: PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthNotes,
                  text: PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthNotes,
                  answer: [{ valueString: form.postpartumEmotionalHealthNotes }],
                },
              ]
            : []),
        ],
      },
    ],
  };

  if (questionnaireResponse?.id) {
    updatedQuestionnaireResponse.id = questionnaireResponse.id;
  }

  return updatedQuestionnaireResponse;
};

export const getPostpartumSexualActivityQuestionnaireResponse = ({
  form,
  clientId,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetPostpartumSexualActivityQuestionnaireResponseParams): QuestionnaireResponse => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    ...(questionnaireResponse?.meta ? { meta: questionnaireResponse.meta } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      {
        linkId: PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivity,
        text: PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivity,
        item: [
          ...(form.postpartumSexualActivityResumed
            ? [
                {
                  linkId: PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityResumed,
                  text: PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityResumed,
                  answer: [{ valueString: form.postpartumSexualActivityResumed }],
                },
              ]
            : []),
          ...(form.postpartumSexualActivityDiscomfort
            ? [
                {
                  linkId:
                    PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityDiscomfort,
                  text: PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityDiscomfort,
                  answer: [{ valueString: form.postpartumSexualActivityDiscomfort }],
                },
              ]
            : []),
          ...(form.postpartumSexualActivityPain?.length
            ? [
                {
                  linkId: PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityPain,
                  text: PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityPain,
                  answer: form.postpartumSexualActivityPain.map((value) => ({
                    valueString: value,
                  })),
                },
              ]
            : []),
          ...(form.postpartumSexualActivityDryness
            ? [
                {
                  linkId: PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityDryness,
                  text: PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityDryness,
                  answer: [{ valueString: form.postpartumSexualActivityDryness }],
                },
              ]
            : []),
          ...(form.postpartumSexualActivityContraception
            ? [
                {
                  linkId:
                    PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityContraception,
                  text: PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityContraception,
                  answer: [{ valueString: form.postpartumSexualActivityContraception }],
                },
              ]
            : []),
          ...(form.postpartumSexualActivityContraceptionMethod?.length
            ? [
                {
                  linkId:
                    PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityContraceptionMethod,
                  text: PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityContraceptionMethod,
                  answer: form.postpartumSexualActivityContraceptionMethod.map((value) => ({
                    valueString: value,
                  })),
                },
              ]
            : []),
          ...(form.postpartumSexualActivityIUD
            ? [
                {
                  linkId: PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityIUD,
                  text: PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityIUD,
                  answer: [{ valueString: form.postpartumSexualActivityIUD }],
                },
              ]
            : []),
          ...(form.postpartumSexualActivityHormoneFree?.length
            ? [
                {
                  linkId:
                    PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityHormoneFree,
                  text: PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityHormoneFree,
                  answer: form.postpartumSexualActivityHormoneFree.map((value) => ({
                    valueString: value,
                  })),
                },
              ]
            : []),
          ...(form.postpartumSexualActivityProgesteroneContaining?.length
            ? [
                {
                  linkId:
                    PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityProgesteroneContaining,
                  text: PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityProgesteroneContaining,
                  answer: form.postpartumSexualActivityProgesteroneContaining.map((value) => ({
                    valueString: value,
                  })),
                },
              ]
            : []),
          ...(form.postpartumSexualActivityFuture
            ? [
                {
                  linkId: PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityFuture,
                  text: PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityFuture,
                  answer: [{ valueString: form.postpartumSexualActivityFuture }],
                },
              ]
            : []),
          ...(form.postpartumSexualActivityFutureWhen
            ? [
                {
                  linkId:
                    PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityFutureWhen,
                  text: PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityFutureWhen,
                  answer: [{ valueString: form.postpartumSexualActivityFutureWhen }],
                },
              ]
            : []),
          ...(form.postpartumSexualActivityNotes
            ? [
                {
                  linkId: PostpartumSexualActivityQuestionsLinkIds.PostpartumSexualActivityNotes,
                  text: PostpartumSexualActivityQuestionsDisplayNames.PostpartumSexualActivityNotes,
                  answer: [{ valueString: form.postpartumSexualActivityNotes }],
                },
              ]
            : []),
        ],
      },
    ],
  };

  if (questionnaireResponse?.id) {
    updatedQuestionnaireResponse.id = questionnaireResponse.id;
  }

  return updatedQuestionnaireResponse;
};

type GetPostpartumOutputQuestionnaireResponseParams = {
  form: {
    urineNormal?: boolean[];
    urineNormalDetails?: string[];
    urineNotNormalDetails?: string[];
    bowelNormal?: boolean[];
    bowelNormalDetails?: string[];
    bowelNotNormalDetails?: string[];
    numberOfTimes?: number;
    numberOfDays?: number;
    notes?: string;
  };
  clientId: string;
  practitionerId: string;
  encounterId: string;
  questionnaireId: string;
  status: QuestionnaireResponse["status"];
  questionnaireResponse?: QuestionnaireResponse;
};

export const getPostpartumOutputQuestionnaireResponse = ({
  form,
  clientId,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetPostpartumOutputQuestionnaireResponseParams): QuestionnaireResponse => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    status,
    subject: { reference: `Patient/${clientId}` },
    encounter: { reference: `Encounter/${encounterId}` },
    author: { reference: `Practitioner/${practitionerId}` },
    questionnaire: `Questionnaire/${questionnaireId}`,
    item: [
      ...(form.urineNormal?.length
        ? [
            {
              linkId: "UrineNormal",
              text: "Normal",
              answer: form.urineNormal.map((value) => ({ valueBoolean: value })),
            },
          ]
        : []),
      ...(form.urineNormalDetails?.length
        ? [
            {
              linkId: "UrineNormalDetails",
              text: "Normal",
              answer: form.urineNormalDetails.map((value) => ({
                valueString: value,
              })),
            },
          ]
        : []),
      ...(form.urineNotNormalDetails?.length
        ? [
            {
              linkId: "UrineNotNormalDetails",
              text: "Not Normal",
              answer: form.urineNotNormalDetails.map((value) => ({
                valueString: value,
              })),
            },
          ]
        : []),
      ...(form.bowelNormal?.length
        ? [
            {
              linkId: "BowelNormal",
              text: "Normal",
              answer: form.bowelNormal.map((value) => ({ valueBoolean: value })),
            },
          ]
        : []),
      ...(form.bowelNormalDetails?.length
        ? [
            {
              linkId: "BowelNormalDetails",
              text: "Normal",
              answer: form.bowelNormalDetails.map((value) => ({
                valueString: value,
              })),
            },
          ]
        : []),
      ...(form.bowelNotNormalDetails?.length
        ? [
            {
              linkId: "BowelNotNormalDetails",
              text: "NotNormal",
              answer: form.bowelNotNormalDetails.map((value) => ({
                valueString: value,
              })),
            },
          ]
        : []),
      ...(form.numberOfTimes !== undefined
        ? [
            {
              linkId: "NumberOfTimes",
              text: "Number of times",
              answer: [{ valueInteger: form.numberOfTimes }],
            },
          ]
        : []),
      ...(form.numberOfDays !== undefined
        ? [
            {
              linkId: "NumberOfDays",
              text: "Number of days",
              answer: [{ valueInteger: form.numberOfDays }],
            },
          ]
        : []),
      ...(form.notes
        ? [
            {
              linkId: "notes",
              text: "Notes",
              answer: [{ valueString: form.notes }],
            },
          ]
        : []),
    ],
  };

  if (questionnaireResponse?.id) {
    updatedQuestionnaireResponse.id = questionnaireResponse.id;
  }

  return updatedQuestionnaireResponse;
};

export const getPostpartumVitalsQuestionnaireResponse = ({
  form,
  clientId,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetPostpartumVitalsQuestionnaireResponseParams): QuestionnaireResponse => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      ...(form.postpartumVitalsHeartRate != null
        ? [
            {
              linkId: "postpartumVitalsHeartRate",
              text: "Heart Rate",
              answer: [{ valueInteger: form.postpartumVitalsHeartRate }],
            },
          ]
        : []),
      ...(form.postpartumVitalsRespiratoryRate != null
        ? [
            {
              linkId: "postpartumVitalsRespiratoryRate",
              text: "Respiratory Rate",
              answer: [{ valueInteger: form.postpartumVitalsRespiratoryRate }],
            },
          ]
        : []),
      ...(form.postpartumVitalsSystolicBP != null
        ? [
            {
              linkId: "postpartumVitalsSystolicBP",
              text: "Systolic Blood Pressure",
              answer: [{ valueDecimal: form.postpartumVitalsSystolicBP }],
            },
          ]
        : []),
      ...(form.postpartumVitalsDiastolicBP != null
        ? [
            {
              linkId: "postpartumVitalsDiastolicBP",
              text: "Diastolic Blood Pressure",
              answer: [{ valueDecimal: form.postpartumVitalsDiastolicBP }],
            },
          ]
        : []),
      ...(form.postpartumVitalsTemperatureValue != null
        ? [
            {
              linkId: "postpartumVitalsTemperatureValue",
              text: "Temperature Value",
              answer: [{ valueDecimal: form.postpartumVitalsTemperatureValue }],
            },
          ]
        : []),
      ...(form.postpartumVitalsTemperatureUnit
        ? [
            {
              linkId: "postpartumVitalsTemperatureUnit",
              text: "Temperature Unit",
              answer: [{ valueString: form.postpartumVitalsTemperatureUnit }],
            },
          ]
        : []),
      ...(form.postpartumVitalsTemperatureMethod
        ? [
            {
              linkId: "postpartumVitalsTemperatureMethod",
              text: "Temperature Method",
              answer: [{ valueCoding: form.postpartumVitalsTemperatureMethod }],
            },
          ]
        : []),
      ...(form.postpartumVitalsNotes
        ? [
            {
              linkId: "postpartumVitalsNotes",
              text: "Notes",
              answer: [{ valueString: form.postpartumVitalsNotes }],
            },
          ]
        : []),
    ],
  };

  if (questionnaireResponse?.id) {
    updatedQuestionnaireResponse.id = questionnaireResponse.id;
  }

  return updatedQuestionnaireResponse;
};

export const getGynecologicalEncounterVitalsSignQuestionnaireResponse = ({
  clientId,
  form,
  practitionerId,
  encounterId,
  questionnaireId,
  status,
  questionnaireResponse,
}: GetGynecologicalEncounterVitalsSignQuestionnaireResponseParams) => {
  const updatedQuestionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    ...(questionnaireResponse?.id ? { id: questionnaireResponse.id } : {}),
    status,
    subject: { reference: joinReferenceString("Patient", clientId) },
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    encounter: { reference: joinReferenceString("Encounter", encounterId) },
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    authored: new Date().toISOString(),
    item: [
      ...(isNumber(form.gynecologicalVitalsWeight)
        ? [
            {
              linkId: "gynecologicalVitalsWeight",
              text: "Weight",
              answer: [{ valueDecimal: form.gynecologicalVitalsWeight }],
            },
          ]
        : []),
      ...(form.gynecologicalVitalsWeightUnit
        ? [
            {
              linkId: "gynecologicalVitalsWeightUnit",
              text: "Weight Unit",
              answer: [{ valueString: form.gynecologicalVitalsWeightUnit }],
            },
          ]
        : []),
      ...(form.gynecologicalVitalsHeightUnit
        ? [
            {
              linkId: "gynecologicalVitalsHeightUnit",
              text: "Height Unit",
              answer: [{ valueString: form.gynecologicalVitalsHeightUnit }],
            },
          ]
        : []),
      ...(isNumber(form.gynecologicalVitalsHeightFeet)
        ? [
            {
              linkId: "gynecologicalVitalsHeightFeet",
              text: "Height (Feet)",
              answer: [{ valueDecimal: form.gynecologicalVitalsHeightFeet }],
            },
          ]
        : []),
      ...(isNumber(form.gynecologicalVitalsHeightInches)
        ? [
            {
              linkId: "gynecologicalVitalsHeightInches",
              text: "Height (Inches)",
              answer: [{ valueDecimal: form.gynecologicalVitalsHeightInches }],
            },
          ]
        : []),
      ...(isNumber(form.gynecologicalVitalsHeightCm)
        ? [
            {
              linkId: "gynecologicalVitalsHeightCm",
              text: "Height (cm)",
              answer: [{ valueDecimal: form.gynecologicalVitalsHeightCm }],
            },
          ]
        : []),
      ...(form.gynecologicalVitalsSystolicBP != null
        ? [
            {
              linkId: "gynecologicalVitalsSystolicBP",
              text: "Systolic Blood Pressure",
              answer: [{ valueDecimal: form.gynecologicalVitalsSystolicBP }],
            },
          ]
        : []),
      ...(form.gynecologicalVitalsDiastolicBP != null
        ? [
            {
              linkId: "gynecologicalVitalsDiastolicBP",
              text: "Diastolic Blood Pressure",
              answer: [{ valueDecimal: form.gynecologicalVitalsDiastolicBP }],
            },
          ]
        : []),
      ...(isNumber(form.gynecologicalVitalsHeartRate)
        ? [
            {
              linkId: "gynecologicalVitalsHeartRate",
              text: "Heart Rate",
              answer: [{ valueInteger: form.gynecologicalVitalsHeartRate }],
            },
          ]
        : []),
      ...(isNumber(form.gynecologicalVitalsTemperature)
        ? [
            {
              linkId: "gynecologicalVitalsTemperature",
              text: "Temperature",
              answer: [{ valueDecimal: form.gynecologicalVitalsTemperature }],
            },
          ]
        : []),
      ...(form.gynecologicalVitalsTemperatureUnit
        ? [
            {
              linkId: "gynecologicalVitalsTemperatureUnit",
              text: "Temperature Unit",
              answer: [{ valueString: form.gynecologicalVitalsTemperatureUnit }],
            },
          ]
        : []),
      ...(form.gynecologicalVitalsTemperatureMethod?.code
        ? [
            {
              linkId: "gynecologicalVitalsTemperatureMethod",
              text: "Temperature Method",
              answer: [
                {
                  valueCoding: form.gynecologicalVitalsTemperatureMethod,
                },
              ],
            },
          ]
        : []),
      ...(isNumber(form.gynecologicalVitalsRespiratoryRate)
        ? [
            {
              linkId: "gynecologicalVitalsRespiratoryRate",
              text: "Respiratory Rate",
              answer: [{ valueInteger: form.gynecologicalVitalsRespiratoryRate }],
            },
          ]
        : []),
      ...(form.gynecologicalVitalsNotes && form.gynecologicalVitalsNotes.trim() !== ""
        ? [
            {
              linkId: "gynecologicalVitalsNotes",
              text: "Notes",
              answer: [{ valueString: form.gynecologicalVitalsNotes }],
            },
          ]
        : []),
    ],
  };

  return updatedQuestionnaireResponse;
};
